import Cookies from "js-cookie";
import {
  FeatureFlag,
  ClientsCollection,
  ApiKeyValues,
  EngagementType,
  Commission,
  Payout,
} from "../types";

// Add CommissionPayout interface
export interface CommissionPayout {
  id: string; // uuid
  created_at: string; // timestamp
  user_id: number;
  amount: number; // decimal
  status: "pending" | "completed" | "cancelled";
  description?: string;
  completed_at?: string; // timestamp
  cancelled_at?: string; // timestamp
  commissions_paid: string[]; // array of commission IDs that were paid out
}

// Add AgentCommission interface
export interface AgentCommission {
  id: string; // uuid
  created_at: string; // timestamp
  client_id: number;
  user_id: number;
  commission_amount: number; // decimal
  status: string; // enum
  approved_date: string | null; // date
}

interface AgentPayout {
  id: string;
  amount: number;
  status: string;
  created_at: string;
  processed_at: string | null;
  payment_method: string;
  reference: string;
}

export default class XanoAPI {
  private static instance: XanoAPI;
  private readonly XANO_ENDPOINT =
    "https://xzkw-tynl-zqtb.n7c.xano.io/api:UIrow74p/";
  // Private API group for AI/ML endpoints
  private readonly XANO_PRIVATE_ENDPOINT =
    "https://xzkw-tynl-zqtb.n7c.xano.io/api:ChWvOPSl/";
  private secure = this.isProduction();

  private constructor() {}

  /**
   * Returns the singleton instance of the XanoAPI class.
   * @returns {XanoAPI} The singleton instance.
   */
  public static getInstance(): XanoAPI {
    if (!XanoAPI.instance) {
      XanoAPI.instance = new XanoAPI();
    }

    return XanoAPI.instance;
  }

  /**
   * Method to check if the environment is production
   * @returns {boolean} True if production; otherwise, false.
   */
  public isProduction(env?: string): boolean {
    // return true;
    if (env && env === "production") return true;

    if (
      window.location.hostname === "localhost" ||
      window.location.hostname === "dev.app.retireplanningmax.com"
    ) {
      return false;
    }
    return true;
  }

  /**
   * Sets the authentication token in cookies.
   * @param {string} token - The authentication token to set.
   */
  setToken(token: string) {
    Cookies.set("authToken", token, {
      expires: 7,
      secure: this.secure,
      sameSite: "strict",
    });
  }

  /**
   * Retrieves the authentication token from cookies.
   * @returns {string | undefined} The authentication token if it exists.
   */
  getToken(): string | undefined {
    return Cookies.get("authToken");
  }

  /**
   * Removes the authentication token from cookies.
   */
  removeToken() {
    Cookies.remove("authToken");
  }

  /**
   * Sets the user id in cookies.
   * @param {string | number} userId - The user id to set.
   */
  setUserId(userId: string | number): void {
    Cookies.set("userId", userId.toString(), {
      expires: 7,
      secure: this.secure,
      sameSite: "strict",
    });
  }

  /**
   * Retrieves the user id from cookies.
   * @returns {number} The user id if it exists.
   */
  getUserId(): number {
    const userId = Cookies.get("userId");
    return Number(userId);
  }

  /**
   * Removes the user id from cookies.
   */
  removeUserId() {
    Cookies.remove("userId");
  }

  /**
   * Checks if the user is authenticated.
   * @returns {boolean} True if authenticated; otherwise, false.
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /**
   * Signs out the current user by removing the authentication token and user ID.
   */
  signOut() {
    this.removeToken();
    this.removeUserId();
    if (window.location.pathname !== "/sign-in") {
      window.location.href = "/sign-in";
      setTimeout(() => window.location.reload(), 2000);
    }
  }

  /**
   * Handles the response from the API.
   * @param {Response} response - The API response.
   * @returns {Response | null} The handled response or null if unauthorized.
   */
  private handleResponse(response: Response): Response | null {
    if (!response.ok && response.status === 401) {
      this.signOut();
      return null;
    }
    return response;
  }

  /**
   * Gets updated headers for API requests.
   * @returns {Headers} The request headers.
   */
  private get headers(): Headers {
    return new Headers({
      "Content-Type": "application/json",
      "X-Data-Source": this.isProduction() ? "live" : "dev",
      Authorization: `Bearer ${this.getToken()}`,
    });
  }

  /**
   * Fetches the current user's data from the API.
   * @returns {Promise<PlatformUser | null>} The current user's data or null if an error occurs.
   */
  public async getCurrentUser(forceRefresh = false): Promise<PlatformUser | null> {
    try {
      // Add cache-busting query param if force refresh is requested
      const endpoint = forceRefresh ? `auth/me?t=${Date.now()}` : "auth/me";
      return await this.apiGet<PlatformUser>(endpoint, (data) => {
        if (data && data.id) this.setUserId(data.id);

        // Ensure admin users have all necessary permissions
        if (data?.user_permissions?.[0]?.role === "admin") {
          // Ensure admin has all permissions
          if (data.user_permissions[0]) {
            data.user_permissions[0].can_manage_users = true;
            data.user_permissions[0].can_manage_clients = true;
            data.user_permissions[0].can_view_reports = true;
            data.user_permissions[0].can_edit_preferences = true;
            data.user_permissions[0].can_manage_billing = true;
          }

          // Ensure admin has all feature modules
          data.module_federal_plan = true;
          data.module_401k_plan = true;
          data.module_403b_plan = true;
          data.module_crm_plan = true;
        }

        return data;
      });
    } catch (error: any) {
      // Only clear tokens for actual auth errors (401/403)
      // Don't clear on network errors like "Failed to fetch"
      if (
        error?.status === 401 ||
        error?.status === 403 ||
        error?.message?.toLowerCase().includes("unauthorized")
      ) {
        console.warn("⚠️ Auth token expired or invalid - clearing credentials");
        this.removeToken();
        this.removeUserId();
      } else {
        // For network errors, log but don't clear the token
        console.error("❌ Error getting current user (non-auth error):", error);
      }
      return null;
    }
  }

  /**
   * ✅ ACTIVE METHOD - Fetches feature flags for the current user
   * Used by: AuthContext for determining UI element visibility
   * @returns {Promise<Array<FeatureFlag>>} Array of feature flags available to the user
   */
  public async getFeatureFlags(): Promise<Array<FeatureFlag>> {
    try {
      const flags = await this.apiGet<Array<FeatureFlag>>("feature_flags");
      return flags;
    } catch (error) {
      console.error("Error fetching feature flags:", error);
      return [];
    }
  }

  /**
   * Retrieves all client records from the API.
   * @returns {Promise<Array<ClientType>>} A list of clients.
   */
  // public async getAllClients(): Promise<ClientsCollection> {
  //   return this.apiGet<ClientsCollection>("client");
  // }

  // public async getAllClients(
  //   page: number = 1,
  //   search: string = "",
  //   perPage: number = 10,
  //   sort: string = "403b"
  // ): Promise<ClientsCollection> {
  //   try {
  //     const queryParams = new URLSearchParams();
  //     queryParams.append("page", String(page));
  //     queryParams.append("perPage", String(perPage));
  //     queryParams.append("sort", String(sort));

  //     if (search) {
  //       const searchFilter = JSON.stringify({
  //         expression: [
  //           {
  //             statement: {
  //               left: { tag: "col", operand: "lastName" },
  //               op: "contains",
  //               right: { operand: search },
  //             },
  //           },
  //         ],
  //       });
  //       console.log(encodeURIComponent(searchFilter));
  //       queryParams.append("search", searchFilter); // ❌ No encodeURIComponent
  //     }

  //     const requestUrl = `${this.XANO_ENDPOINT}client?${queryParams.toString()}`;
  //     console.log("🌐 API REQUEST URL:", requestUrl);

  //     const response = await fetch(requestUrl, {
  //       method: "GET",
  //       headers: this.headers,
  //     });

  //     if (!response.ok) {
  //       throw new Error("Failed to fetch clients");
  //     }

  //     return await response.json();
  //   } catch (error) {
  //     console.error("❌ API ERROR:", error);
  //     return { items: [] };
  //   }
  // }

  public async getAllClients(
    page: number = 1,
    search: string = "",
    perPage: number = 10,
    filters: {
      status?: string;
      lifecycleStage?: string;
      agency?: string;
      state?: string;
      planType?: string;
      date_from?: string;
      date_to?: string;
      commission_approved?: boolean;
      amount_range?: [string, string]; // Range filtering with between operation
      age_min?: number;
      age_max?: number;
      commission_min?: number;
      commission_max?: number;
    } = {},
    userId?: string // New userId param for filtering by agent
  ): Promise<ClientsCollection> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page", String(page));
      queryParams.append("perPage", String(perPage));

      // Add userId param if provided (for admin filtering by agent)
      if (userId) {
        queryParams.append("userId", userId);
        // console.log("🔍 Filtering clients by userId:", userId);
      }

      // ✅ ENHANCED: Construct JSON structure for Xano filtering with more filter types
      const filterExpressions: any[] = [];

      // Add search filter if provided
      if (search) {
        // Search across multiple fields (lastName, firstName, email)
        filterExpressions.push({
          statement: {
            left: { tag: "col", operand: "client.lastName" },
            op: "contains",
            right: { operand: search },
          },
          type: "statement",
          or: false, // Explicitly specify AND operation
        });
        // console.log("🔍 Added search filter for:", search);
      }

      // Add other filters if provided
      if (filters) {
        Object.entries(filters)
          .filter(([_, value]) => {
            // ✅ FIXED: Only filter out truly empty values, not valid filter values
            return value !== undefined && value !== null && value !== "";
          })
          .forEach(([key, value]) => {
            let operand = `client.${key}`;
            let operation = "=";
            let rightOperand = value;

            // Handle special filter types and operations
            if (key === "date_from") {
              operand = "client.created_at";
              operation = ">=";
            } else if (key === "date_to") {
              operand = "client.created_at";
              operation = "<=";
            } else if (key === "commission_approved") {
              operation = "=";
              // Convert boolean to string for API
              rightOperand = value ? "true" : "false";
            } else if (key === "amount_range") {
              // Handle range filtering (between operation)
              if (Array.isArray(value) && value.length === 2) {
                operand = "client.amount";
                operation = "between";
                rightOperand = [String(value[0]), String(value[1])];
              }
            } else if (key === "age_min") {
              operand = "client.age";
              operation = ">=";
            } else if (key === "age_max") {
              operand = "client.age";
              operation = "<=";
            } else if (key === "commission_min") {
              operand = "client.commission_amount";
              operation = ">=";
            } else if (key === "commission_max") {
              operand = "client.commission_amount";
              operation = "<=";
            }

            console.log(
              `🔍 Adding filter: ${key} = ${JSON.stringify(rightOperand)} (operand: ${operand}, op: ${operation})`
            );

            filterExpressions.push({
              statement: {
                left: { tag: "col", operand },
                op: operation,
                right: { operand: rightOperand },
              },
              type: "statement",
              or: false, // Explicitly specify AND operation
            });
          });
      }

      // Add JSON filter to query params if we have any filters
      if (filterExpressions.length > 0) {
        const filterJson = JSON.stringify({ expression: filterExpressions });
        // console.log("🔍 FILTER JSON:", filterJson);
        queryParams.append("search", filterJson);
      }

      const finalUrl = `${this.XANO_ENDPOINT}client?${queryParams.toString()}`;

      // 🔍 COMPREHENSIVE LOGGING: Show exact Xano call details
      console.log("=".repeat(80));
      console.log("🌐 EXACT XANO API CALL DETAILS");
      console.log("=".repeat(80));
      console.log("🔗 FULL URL:", finalUrl);
      console.log("🔗 BASE ENDPOINT:", this.XANO_ENDPOINT);
      console.log("🔗 ENDPOINT PATH:", "client");
      console.log("🔗 QUERY PARAMETERS:", queryParams.toString());
      console.log(
        "🔗 DECODED SEARCH PARAM:",
        decodeURIComponent(queryParams.get("search") || "")
      );
      console.log("🔗 REQUEST METHOD:", "GET");
      console.log("🔗 REQUEST HEADERS:", this.headers);
      console.log("=".repeat(80));
      // console.log("🔍 FILTER EXPRESSIONS:", filterExpressions);
      console.log("📋 RAW FILTERS RECEIVED:", filters);
      // console.log("🔍 FILTER EXPRESSIONS COUNT:", filterExpressions.length);
      console.log("=".repeat(80));

      // 🔍 LOG EXACT FETCH CALL
      const fetchUrl = `${this.XANO_ENDPOINT}client?${queryParams.toString()}`;
      const fetchOptions = {
        method: "GET",
        headers: this.headers,
      };

      console.log("🚀 EXECUTING FETCH CALL:");
      console.log("🚀 URL:", fetchUrl);
      console.log("🚀 OPTIONS:", fetchOptions);
      console.log("🚀 HEADERS:", JSON.stringify(this.headers, null, 2));

      // 🔍 EXACT URL FOR FILTERING - COPY THIS URL TO TEST
      console.log("=".repeat(100));
      console.log("🔗 EXACT URL FOR FILTERING (COPY THIS TO TEST):");
      console.log("=".repeat(100));
      console.log(fetchUrl);
      console.log("=".repeat(100));
      console.log("🔗 DECODED SEARCH PARAMETER:");
      console.log(decodeURIComponent(queryParams.get("search") || ""));
      console.log("=".repeat(100));

      const response = await fetch(fetchUrl, fetchOptions);

      // 🔍 DEBUG: Log response details
      // console.log("🔍 RESPONSE STATUS:", response.status);
      // console.log("🔍 RESPONSE OK:", response.ok);
      console.log(
        "🔍 RESPONSE HEADERS:",
        Object.fromEntries(response.headers.entries())
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ RESPONSE ERROR TEXT:", errorText);
        throw new Error(
          `Failed to fetch clients: ${response.status} ${response.statusText}`
        );
      }

      const responseData = await response.json();
      // console.log("🔍 RESPONSE DATA:", responseData);
      // console.log("🔍 RESPONSE ITEMS COUNT:", responseData?.items?.length || 0);
      // console.log("🔍 RESPONSE DATA TYPE:", typeof responseData);
      console.log(
        "🔍 RESPONSE DATA KEYS:",
        responseData ? Object.keys(responseData) : "No data"
      );

      // Log first few items for debugging
      if (responseData?.items && Array.isArray(responseData.items)) {
        // console.log("🔍 FIRST 3 ITEMS:", responseData.items.slice(0, 3));
        // console.log("🔍 TOTAL ITEMS:", responseData.items.length);
      }

      return responseData;
    } catch (error) {
      console.error("❌ API ERROR:", error);
      return { items: [] };
    }
  }

  /**
   * Fetches clients for dropdown/lookup components (optimized for large lists).
   * Fetches all clients using pagination if necessary.
   * @param {string} userId - Optional user ID for role-based filtering (agents see only their clients)
   * @returns {Promise<Array<ClientType>>} - A list of all clients.
   */
  public async getClientsForLookup(
    userId?: string
  ): Promise<Array<ClientType>> {
    try {
      let allClients: ClientType[] = [];
      let currentPage = 1;
      let hasMorePages = true;
      const perPage = 1000; // Try to fetch 1000 at a time

      console.log("📋 Fetching clients for lookup...");
      if (userId) {
        console.log("   Filtered by userId:", userId);
      } else {
        console.log("   Admin view (all clients)");
      }

      // Fetch all pages
      while (hasMorePages) {
        const queryParams = new URLSearchParams();
        queryParams.append("perPage", String(perPage));
        queryParams.append("page", String(currentPage));

        // Add userId param if provided (for agent filtering)
        if (userId) {
          queryParams.append("userId", userId);
        }

        const requestUrl = `${this.XANO_ENDPOINT}client?${queryParams.toString()}`;
        console.log(`📋 Fetching page ${currentPage}:`, requestUrl);
        
        const response = await fetch(requestUrl, {
          method: "GET",
          headers: this.headers,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch clients for lookup: ${response.statusText}`);
        }

        const data: ClientsCollection = await response.json();
        console.log(`   Page ${currentPage}: ${data.items?.length || 0} clients`);
        console.log(`   Has nextPage:`, data.nextPage);
        
        if (data.items && data.items.length > 0) {
          allClients = [...allClients, ...data.items];
        }

        // Check if there are more pages
        if (data.nextPage) {
          currentPage++;
        } else {
          hasMorePages = false;
        }

        // Safety check: prevent infinite loops
        if (currentPage > 100) {
          console.warn("⚠️ Reached maximum page limit (100), stopping pagination");
          break;
        }
      }

      console.log(`✅ Loaded total of ${allClients.length} clients for lookup`);
      
      return allClients;
    } catch (error) {
      console.error("Error fetching clients for lookup:", error);
      throw error;
    }
  }

  /**
   * Fetches all clients associated with a specific agency.
   * @param {number} agencyId - The ID of the agency.
   * @returns {Promise<Array<ClientType>>} - A list of clients.
   */
  public async getClientsByAgencyId(
    agencyId: number
  ): Promise<Array<ClientType>> {
    try {
      const response = await fetch(
        `${this.XANO_ENDPOINT}agency/${agencyId}/clients`,
        {
          method: "GET",
          headers: this.headers,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch clients for agency.");
      }

      return await response.json();
    } catch (error) {
      console.error(`Error fetching clients for agency ID ${agencyId}:`, error);
      throw error;
    }
  }

  /**
   * Fetches a client's data by ID.
   * @param {number | string} clientId - The client's ID.
   * @returns {Promise<ClientType | null>} The client's data or null if an error occurs.
   */
  public async getClient(
    clientId: number | string
  ): Promise<ClientType | null> {
    return this.apiGet<ClientType>(`client/${clientId}`);
  }

  /**
   * Fetches the clients for a specific team member by their ID.
   * @param {number} user_id - The ID of the team member.
   * @returns {Promise<Array<Client>>} A list of clients for the team member.
   */
  public async getClientsByMemberId(
    user_id: number
  ): Promise<Array<ClientType>> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}clients/${user_id}`, {
        method: "GET",
        headers: this.headers,
      });

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) throw new Error("Unauthorized request");

      const data = await handledResponse.json();
      return data;
    } catch (error) {
      console.error(`Error fetching clients for member ${user_id}:`, error);
      throw error;
    }
  }

  /**
   * Adds a new client record to the API.
   * @param {ClientType} clientData - The data for the new client.
   * @returns {Promise<ClientType>} The newly added client data.
   */
  public async addClient(clientData: ClientType): Promise<ClientType> {
    // Set default lifecycle stage if not provided
    const clientDataWithDefaults = {
      ...clientData,
      lifecycleStage: clientData.lifecycleStage || "prospect",
    };

    return this.apiPost<ClientType>("client", clientDataWithDefaults);
  }

  /**
   * Updates a client's data by their ID.
   * @param {string} clientId - The ID of the client to update.
   * @param {Partial<ClientType>} clientData - The client data to be updated.
   * @returns {Promise<ClientType>} The updated client data.
   */
  public async updateClient(
    clientId: number | string,
    clientData: Partial<ClientType>
  ): Promise<ClientType> {
    return this.apiPut<ClientType>(`client/${clientId}`, clientData);
  }

  /**
   * Reassigns a client to a different agent.
   * @param {number | string} clientId - The ID of the client to reassign.
   * @param {number} newAgentId - The ID of the agent/user to assign the client to.
   * @returns {Promise<ClientType>} The updated client data.
   */
  public async reassignClient(
    clientId: number | string,
    newAgentId: number
  ): Promise<ClientType> {
    return this.apiPatch<ClientType>(`client/${clientId}/reassign`, {
      client_id: clientId,
      new_agent_id: newAgentId,
    });
  }

  /**
   * Smart Paste Client - Uses AI to parse email/text content into platform-ready client data.
   * 
   * This endpoint leverages an AI model to intelligently extract structured client information
   * from unstructured text. The AI can identify and parse:
   * - Personal details: firstName, lastName (includes middle name if present), age, dateOfBirth
   * - Contact information: email, phone, cellPhone
   * - Employment: federalAgency (mapped to platform enum values), employmentStatus
   * - Retirement planning: targetRetirementAge, retirementDate
   * - Family details: spousesName, spousesDateOfBirth, numberOfDependents
   * - Address: street, city, state, zip
   * - Financial data: income, assets, account types
   * 
   * Note: The AI intelligently maps values to platform-specific formats:
   * - Federal agencies are mapped to enum values (e.g., "FAA" → "federal_aviation_administration")
   * - Ages are converted to dates of birth when possible
   * - Partial information returns null for missing fields
   * 
   * @param {string} emailContent - The raw text content to be parsed by the AI model.
   *                                Can include emails, notes, or any unstructured client information.
   * @returns {Promise<Partial<ClientType>>} A promise that resolves with the AI-parsed client data.
   *                                          Returns a partial ClientType object with extracted fields.
   * @throws {Error} Throws an error if the API request fails or AI parsing is unsuccessful.
   * 
   * @example
   * ```typescript
   * const textContent = `
   *   Hi there - I'm John Franklin Smith, age 41 and working at the FAA. 
   *   I am hoping to retire by 65. I need to care for my wife who is 56 now.
   * `;
   * 
   * const parsedData = await xanoAPI.smartPasteClient(textContent);
   * // Returns: { 
   * //   firstName: "John",
   * //   lastName: "Franklin Smith",
   * //   age: 41,
   * //   federalAgency: "federal_aviation_administration",
   * //   targetRetirementAge: 65,
   * //   spousesName: "wife",
   * //   spousesDateOfBirth: null  // Age 56 mentioned but not enough for full DOB
   * // }
   * ```
   */
  public async smartPasteClient(
    emailContent: string
  ): Promise<Partial<ClientType>> {
    try {
      // Smart Paste is in the private API group (api:ChWvOPSl)
      const endpoint = `${this.XANO_PRIVATE_ENDPOINT}smart_paste_client`;
      
      console.log("🤖 Smart Paste: Starting request...");
      console.log("📧 Content length:", emailContent.length, "characters");
      console.log("🌐 Endpoint URL:", endpoint);
      console.log("🔑 Has auth token:", !!this.headers.get("Authorization"));

      const requestBody = { input: emailContent };
      console.log("📤 Request body:", JSON.stringify(requestBody).substring(0, 100) + "...");

      const response = await fetch(endpoint, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify(requestBody),
      });

      console.log("📥 Response status:", response.status, response.statusText);
      console.log("📥 Response headers:", Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Smart Paste Error Response:", errorText);
        console.error("❌ Status Code:", response.status);
        console.error("❌ Status Text:", response.statusText);
        
        // Try to parse error as JSON for better error messages
        let errorMessage = `Smart paste failed: ${response.status} ${response.statusText}`;
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.message) {
            errorMessage = errorJson.message;
          }
          console.error("❌ Parsed Error:", errorJson);
        } catch (e) {
          // Error text is not JSON, use as-is
          console.error("❌ Raw Error Text:", errorText);
        }
        
        throw new Error(errorMessage);
      }

      const parsedData = await response.json();
      console.log("✅ Smart Paste: Successfully parsed client data");
      console.log("📊 Extracted fields:", Object.keys(parsedData).join(", "));
      console.log("📊 Parsed data:", parsedData);

      return parsedData;
    } catch (error) {
      console.error("❌ Smart Paste API Error:", error);
      console.error("❌ Error type:", error instanceof Error ? error.constructor.name : typeof error);
      console.error("❌ Error message:", error instanceof Error ? error.message : String(error));
      
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error("Network error: Unable to connect to Smart Paste API. Please check your connection.");
      }
      
      throw error;
    }
  }

  /**
   * Parse Dashboard Email - Uses AI to parse email/text content into dashboard analytics data.
   * 
   * This endpoint leverages an AI model to intelligently extract structured dashboard
   * analytics data from unstructured text/email content. The AI can identify and parse:
   * - Retirement Readiness: Fully funded, minor gap, significant gap clients
   * - AUM by Product: TSP, 401k, 403b, Annuity amounts and percentages
   * - Contribution Rate Distribution: Client counts by contribution rate bands
   * - Social Security Claiming: Distribution by claiming age (62, FRA, 70, unknown)
   * - Retirement Income Sources: Average annual income by source type
   * - FEGLI Risk: Low, moderate, high risk client counts
   * - Age Distribution: Client counts by age buckets
   * - Rollover Opportunities: Top clients with rollover values
   * - Employer Match Utilization: No match, under match, at/above match counts
   * - Location Insights: Client distribution by state/location
   * - Plan Type Spread: FERS, CSRS, 401k, 403b distribution
   * - Longevity Stress Test: At-risk vs funded client counts
   * 
   * The parsed data can be used to populate dashboard charts with real analytics data
   * extracted from emails or reports.
   * 
   * @param {string} emailContent - The raw text/email content containing dashboard analytics data.
   * @returns {Promise<object>} A promise that resolves with the AI-parsed dashboard data object.
   * @throws {Error} Throws an error if the API request fails or AI parsing is unsuccessful.
   * 
   * @example
   * ```typescript
   * const emailContent = `
   *   RETIREMENT READINESS OVERVIEW:
   *   - Fully Funded Clients (≥90%): 25 clients (33.3% of total)
   *   - Minor Gap Clients (70-89.9%): 30 clients (40.0% of total)
   *   AUM BY PRODUCT TYPE:
   *   - TSP: $45,000,000 (45.0% of total)
   *   - 401(k): $25,000,000 (25.0% of total)
   * `;
   * 
   * const parsedData = await xanoAPI.parseDashboardEmail(emailContent);
   * // Returns structured dashboard data object
   * ```
   */
  public async parseDashboardEmail(
    emailContent: string
  ): Promise<object> {
    try {
      // Dashboard email parsing is in the private API group (api:ChWvOPSl)
      const endpoint = `${this.XANO_PRIVATE_ENDPOINT}parse_dashboard_email`;
      
      console.log("📊 Dashboard Email Parser: Starting request...");
      console.log("📧 Content length:", emailContent.length, "characters");
      console.log("🌐 Endpoint URL:", endpoint);
      console.log("🔑 Has auth token:", !!this.headers.get("Authorization"));

      const requestBody = { input: emailContent };
      console.log("📤 Request body preview:", JSON.stringify(requestBody).substring(0, 200) + "...");

      const response = await fetch(endpoint, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify(requestBody),
      });

      console.log("📥 Response status:", response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Dashboard Email Parser Error Response:", errorText);
        console.error("❌ Status Code:", response.status);
        
        // Try to parse error as JSON for better error messages
        let errorMessage = `Dashboard email parsing failed: ${response.status} ${response.statusText}`;
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.message) {
            errorMessage = errorJson.message;
          }
          console.error("❌ Parsed Error:", errorJson);
        } catch (e) {
          console.error("❌ Raw Error Text:", errorText);
        }
        
        throw new Error(errorMessage);
      }

      const parsedData = await response.json();
      console.log("✅ Dashboard Email Parser: Successfully parsed dashboard data");
      console.log("📊 Extracted widgets:", Object.keys(parsedData).join(", "));
      console.log("📊 Parsed data:", parsedData);

      return parsedData;
    } catch (error) {
      console.error("❌ Dashboard Email Parser API Error:", error);
      console.error("❌ Error type:", error instanceof Error ? error.constructor.name : typeof error);
      console.error("❌ Error message:", error instanceof Error ? error.message : String(error));
      
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new Error("Network error: Unable to connect to Dashboard Email Parser API. Please check your connection.");
      }
      
      throw error;
    }
  }

  /**
   * Sends a message to the AI chat agent and receives a response.
   * 
   * This method interacts with the "Client Analysis Agent" AI agent via the private API.
   * It either continues an existing chat thread (if thread_id is provided) or creates a new one.
   * The backend maintains conversation history for AI context, but the frontend should
   * treat each session as ephemeral (not displaying past history).
   * 
   * @param {string} message - The user's message to send to the AI agent.
   * @param {string} [threadId] - Optional thread ID to continue an existing conversation.
   *                              If not provided, a new thread will be created.
   * 
   * @returns {Promise<{result: string, thread_id: string}>} A promise that resolves to:
   *   - `result`: The AI agent's response message
   *   - `thread_id`: The thread ID for this conversation (use this to continue the thread)
   * 
   * @throws {Error} Throws an error if:
   *   - The user is not authenticated
   *   - The API request fails
   *   - The response cannot be parsed
   * 
   * @example
   * ```typescript
   * const api = XanoAPI.getInstance();
   * 
   * // Start a new conversation
   * const response1 = await api.sendChatMessage("Find me a client named John Smith");
   * console.log(response1.result); // AI response
   * console.log(response1.thread_id); // Save this to continue conversation
   * 
   * // Continue the conversation
   * const response2 = await api.sendChatMessage(
   *   "What's his email address?",
   *   response1.thread_id
   * );
   * ```
   * 
   * @see {@link https://xzkw-tynl-zqtb.n7c.xano.io/workspace/1-0/api/5/query/109} Xano Editor URL
   * 
   * @since 1.0.0
   */
  public async sendChatMessage(
    message: string,
    threadId?: string
  ): Promise<{ result: string; thread_id: string }> {
    try {
      // Chat endpoint is in the private API group (api:ChWvOPSl)
      const endpoint = `${this.XANO_PRIVATE_ENDPOINT}chat/send_message`;

      // Validate authentication
      if (!this.isAuthenticated()) {
        throw new Error("User must be authenticated to send chat messages");
      }

      // Prepare request body
      // NOTE: We're intentionally NOT sending thread_id to avoid context window issues
      // The backend accumulates too much history including full client data objects
      const requestBody: { message: string } = {
        message: message.trim(),
      };

      // TEMPORARY: Disabled thread_id to prevent context window errors
      // Backend needs to implement conversation summarization or truncation
      // if (threadId) {
      //   requestBody.thread_id = threadId;
      // }
      
      if (threadId) {
        console.warn("⚠️ Thread ID provided but NOT being sent to prevent context window errors:", threadId);
        console.warn("⚠️ Backend accumulates too much history. Each request will start fresh.");
      }

      console.log("=".repeat(80));
      console.log("💬 CHAT API REQUEST DEBUG");
      console.log("=".repeat(80));
      console.log("📝 Message:", message);
      console.log("📏 Message length:", message.length, "characters");
      console.log("🧵 Thread ID parameter received:", threadId || "none");
      console.log("🧵 Thread ID being sent:", "NONE (disabled to prevent context window errors)");
      console.log("🌐 Endpoint URL:", endpoint);
      console.log("🔑 Has auth token:", !!this.headers.get("Authorization"));
      console.log("🔑 Auth token preview:", this.getToken() ? `${this.getToken()?.substring(0, 20)}...` : "none");
      console.log("📦 Request body (NO thread_id):", JSON.stringify(requestBody, null, 2));
      console.log("📋 Request headers:", {
        "Content-Type": this.headers.get("Content-Type"),
        "Authorization": this.headers.get("Authorization") ? "Bearer [REDACTED]" : "none",
        "X-Data-Source": this.headers.get("X-Data-Source"),
      });
      console.log("=".repeat(80));
      console.log("⚠️ IMPORTANT: thread_id is NOT being sent to prevent context window errors");
      console.log("⚠️ Backend should start fresh conversation, but may still include previous history");
      console.log("=".repeat(80));

      const response = await fetch(endpoint, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify(requestBody),
      });

      console.log("=".repeat(80));
      console.log("📥 CHAT API RESPONSE DEBUG");
      console.log("=".repeat(80));
      console.log("📊 Status:", response.status, response.statusText);
      console.log("📋 Response headers:", Object.fromEntries(response.headers.entries()));
      console.log("=".repeat(80));

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Chat Error Response:", errorText);
        console.error("❌ Status Code:", response.status);
        console.error("❌ Status Text:", response.statusText);

        // Try to parse error as JSON for better error messages
        let errorMessage = `Chat request failed: ${response.status} ${response.statusText}`;
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.message) {
            errorMessage = errorJson.message;
          } else if (errorJson.error) {
            errorMessage = errorJson.error;
          }
          console.error("❌ Parsed Error:", errorJson);
        } catch (e) {
          // Error text is not JSON, use as-is
          console.error("❌ Raw Error Text:", errorText);
        }

        // Handle authentication errors
        if (response.status === 401) {
          this.signOut();
          throw new Error("Authentication failed. Please sign in again.");
        }

        throw new Error(errorMessage);
      }

      const responseText = await response.text();
      console.log("📄 Raw response text length:", responseText.length, "characters");
      console.log("📄 Raw response preview:", responseText.substring(0, 200));
      
      let data;
      try {
        data = JSON.parse(responseText);
        console.log("✅ Successfully parsed JSON response");
        console.log("📦 Parsed response data:", JSON.stringify(data, null, 2));
      } catch (parseError) {
        console.error("❌ Failed to parse JSON response:", parseError);
        console.error("❌ Raw response:", responseText);
        throw new Error(`Invalid JSON response: ${parseError instanceof Error ? parseError.message : String(parseError)}`);
      }
      
      // Validate response structure
      if (!data.result || !data.thread_id) {
        console.warn("⚠️ Unexpected response structure:");
        console.warn("⚠️ Expected: { result: string, thread_id: string }");
        console.warn("⚠️ Received:", data);
        console.warn("⚠️ Response keys:", Object.keys(data));
        throw new Error("Invalid response format from chat API");
      }

      console.log("=".repeat(80));
      console.log("✅ CHAT API SUCCESS");
      console.log("=".repeat(80));
      console.log("📝 Result length:", data.result.length, "characters");
      console.log("📝 Result preview:", data.result.substring(0, 100) + (data.result.length > 100 ? "..." : ""));
      console.log("🧵 Thread ID:", data.thread_id);
      console.log("=".repeat(80));

      return {
        result: data.result,
        thread_id: data.thread_id,
      };
    } catch (error) {
      console.error("❌ Error in sendChatMessage:", error);
      throw error;
    }
  }

  /**
   * Deletes a client record by their ID.
   * @param {string} clientId - The ID of the client to delete.
   * @returns {Promise<void>} Resolves when the client is deleted.
   */
  public async deleteClient(clientId: string): Promise<void> {
    return this.apiDelete(`client/${clientId}`);
  }

  /**
   * Attempts to log in with the provided credentials.
   * @param {AuthUser} formData - The login credentials.
   * @returns {Promise<any>} The response from the login attempt.
   */
  public async login(formData: AuthUser): Promise<any> {
    const data = await this.apiPost<any>("auth/login", formData);
    if (data && data.authToken) {
      this.setToken(data.authToken);
    }
    return data;
  }

  public async validateLoginTOTP(
    mfaLoginToken: string,
    totpCode: string
  ): Promise<{ authToken?: string }> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}auth/login_totp`, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify({
          mfa_login_token: mfaLoginToken,
          totp_code: totpCode,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to validate TOTP code.");
      }

      const data = await response.json();
      return {
        authToken: data,
      };
    } catch (error) {
      console.error("Error validating TOTP login:", error);
      throw error;
    }
  }
  /**
   * Adds a new feedback record to the API.
   * @param {Object} feedbackData - The data for the new feedback.
   * @returns {Promise<Object>} The newly added feedback data.
   */
  public async addFeedback(feedbackData: {
    feedback_text: string;
  }): Promise<Object> {
    return this.apiPost<Object>("feedback", feedbackData);
  }

  /**
   * Queries all feedback records from the API.
   * @returns {Promise<Array<Object>>} An array of feedback records.
   */
  public async getFeedback(): Promise<Array<Object>> {
    return this.apiGet<Array<Object>>("feedback");
  }

  /**
   * Deletes a feedback record by ID.
   * @param {string} feedbackId - The ID of the feedback to delete.
   * @returns {Promise<void>} Resolves when the feedback record is deleted.
   */
  public async deleteFeedback(feedbackId: string): Promise<void> {
    return this.apiDelete(`feedback/${feedbackId}`);
  }

  public async signUp(invite_code: string, password: string): Promise<any> {
    // console.log(inviteCode, password);
    const signupData = {
      invite_code,
      password,
    };

    return this.apiPut<any>("auth/agent_accept", signupData);
  }

  public async getTeamMemberById(userId: number): Promise<TeamMember | null> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}user/${userId}`, {
        method: "GET",
        headers: this.headers,
      });

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) throw new Error("Unauthorized request");

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error fetching user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Updates the user's data by their ID.
   * @param {string} userId - The user's ID.
   * @param {Partial<PlatformUser>} userData - The user data to be updated.
   * @returns {Promise<PlatformUser>} The updated user data.
   */
  public async updateUser(
    userId: string | number,
    userData: Partial<PlatformUser>
  ): Promise<PlatformUser> {
    return this.apiPut<PlatformUser>(`user/${userId}`, userData);
  }

  /**
   * Updates the dashboard layout for a user.
   * @param {number} userId - The user's ID.
   * @param {Object} layout - The dashboard layout object to save.
   * @returns {Promise<PlatformUser>} The updated user data.
   */
  public async updateUserDashboardLayout(
    userId: number,
    layout: any
  ): Promise<PlatformUser> {
    // Store layout as JSON string in user record
    return this.apiPatch<PlatformUser>(`user/${userId}`, {
      dashboard_layout: JSON.stringify(layout),
    });
  }

  /**
   * Saves dashboard layout data to the user's dashboards field.
   * Uses the /user/{user_id}/dashboards PUT endpoint.
   * @param {number} userId - The user's ID.
   * @param {Object} dashboards - The dashboard layout object to save (will be stringified).
   * @returns {Promise<string>} The saved dashboards JSON string.
   */
  public async saveUserDashboards(
    userId: number,
    dashboards: any
  ): Promise<string> {
    try {
      console.log(`💾 Saving dashboard layout for user ${userId}`);
      console.log(`💾 Widget count: ${dashboards?.widgets?.length || 0}`);
      
      // Log visibility states and column layout being saved
      if (dashboards?.widgets) {
        const hiddenWidgets = dashboards.widgets.filter((w: any) => w.visible === false).map((w: any) => w.widgetId);
        console.log(`💾 Hidden widgets being saved:`, hiddenWidgets.length > 0 ? hiddenWidgets.join(',') : 'none');
        console.log(`💾 Column layout being saved:`, JSON.stringify(dashboards.cols));
      }
      
      // Send the dashboard layout object (not stringified)
      // The endpoint expects 'json dashboards' input type, and Xano's db.edit should stringify it
      // when saving to the TEXT database field using JSON.stringify($input.dashboards)
      const response = await this.apiPut<any>(`user/${userId}/dashboards`, {
        dashboards: dashboards,
      });
      
      console.log(`✅ Dashboard layout saved successfully for user ${userId}`);
      console.log(`✅ Response:`, response);
      console.log(`✅ Response type:`, typeof response);
      console.log(`✅ Response has widgets?:`, !!(response && typeof response === 'object' && response.widgets));
      
      // Verify the save actually persisted by fetching user directly (not via auth/me)
      // auth/me might not include dashboards field, so fetch directly from user endpoint
      setTimeout(async () => {
        try {
          // Fetch user directly from /user/{userId} endpoint instead of auth/me
          const verifyUser = await this.getUserById(userId);
          const verifyDashboards = (verifyUser as any)?.dashboards || (verifyUser as any)?.dashboard_layout || (verifyUser as any)?.dashboard;
          // console.log(`🔍 Verification: Checking if data was saved to database...`);
          // console.log(`🔍 Verification: User ID:`, verifyUser ? (verifyUser as any).id : 'no user');
          // console.log(`🔍 Verification: Dashboards field exists?:`, !!verifyDashboards);
          // console.log(`🔍 Verification: Dashboards field type:`, typeof verifyDashboards);
          // console.log(`🔍 Verification: Full user object keys:`, Object.keys(verifyUser || {}));
          
          if (verifyDashboards) {
            const verifyParsed = typeof verifyDashboards === 'string' ? JSON.parse(verifyDashboards) : verifyDashboards;
            console.log(`✅ Verification: Data WAS saved! Widget count:`, verifyParsed?.widgets?.length);
            console.log(`✅ Verification: Widget order:`, verifyParsed?.widgets?.map((w: any) => w?.widgetId).join(','));
          } else {
            console.error(`❌ Verification: Data was NOT saved to database!`);
            console.error(`❌ Verification: User object keys:`, Object.keys(verifyUser || {}));
            console.error(`❌ Verification: This means the backend endpoint is not persisting data`);
            console.error(`❌ Verification: Check Xano endpoint configuration - ensure db.edit is saving to user.dashboards field`);
          }
        } catch (verifyError) {
          console.error("❌ Verification failed:", verifyError);
        }
      }, 2000); // Wait 2 seconds for database to commit
      
      return response;
    } catch (error: any) {
      console.error("❌ Error saving user dashboards:", error);
      console.error("❌ Error details:", {
        message: error?.message,
        status: error?.status,
        url: error?.url,
        response: error?.response,
      });
      throw error;
    }
  }

  /**
   * Fetches dashboard layout data from the user's dashboards field.
   * @param {number} userId - The user's ID.
   * @returns {Promise<any | null>} The parsed dashboards object or null if not found.
   */
  public async getUserDashboards(userId: number): Promise<any | null> {
    try {
      // Fetch user directly from /user/{userId} endpoint to ensure we get all fields including dashboards
      // This is more reliable than getCurrentUser which might not include all fields
      const user = await this.getUserById(userId);
      if (!user || (user as any).id !== userId) {
        console.log(`getUserDashboards: User mismatch or not found. Expected: ${userId}, Got: ${(user as any)?.id}`);
        return null;
      }

      // Check multiple possible field names since backend might use different field
      const dashboardsData = (user as any).dashboards || (user as any).dashboard_layout || (user as any).dashboard;
      console.log(`getUserDashboards: Raw dashboards data type:`, typeof dashboardsData);
      console.log(`getUserDashboards: Raw dashboards data:`, dashboardsData);
      console.log(`getUserDashboards: Full user object keys:`, Object.keys(user || {}));
      console.log(`getUserDashboards: Checking fields - dashboards: ${!!(user as any).dashboards}, dashboard_layout: ${!!(user as any).dashboard_layout}, dashboard: ${!!(user as any).dashboard}`);
      
      if (!dashboardsData) {
        console.log("getUserDashboards: No dashboards data found in user record");
        console.log("getUserDashboards: User object:", user);
        console.warn("⚠️ Backend endpoint may not be saving to database. Check Xano endpoint configuration.");
        return null;
      }

      // Parse JSON if it's a string
      let parsedData: any;
      if (typeof dashboardsData === "string") {
        try {
          parsedData = JSON.parse(dashboardsData);
          console.log("getUserDashboards: Parsed JSON string, widget count:", parsedData?.widgets?.length);
          console.log("getUserDashboards: Parsed widget order:", parsedData?.widgets?.map((w: any) => w?.widgetId).join(','));
        } catch (parseError) {
          console.error("getUserDashboards: Failed to parse JSON string:", parseError);
          return null;
        }
      } else {
        parsedData = dashboardsData;
        console.log("getUserDashboards: Data is already an object, widget count:", parsedData?.widgets?.length);
        console.log("getUserDashboards: Widget order:", parsedData?.widgets?.map((w: any) => w?.widgetId).join(','));
      }
      
      // Validate that parsed data has widgets array
      if (parsedData && parsedData.widgets && Array.isArray(parsedData.widgets)) {
        console.log("getUserDashboards: Valid layout found with", parsedData.widgets.length, "widgets");
        console.log("getUserDashboards: Returning layout with cols:", JSON.stringify(parsedData.cols));
        
        // Log visibility states for debugging
        const hiddenWidgets = parsedData.widgets.filter((w: any) => w.visible === false).map((w: any) => w.widgetId);
        console.log("getUserDashboards: Hidden widgets in returned layout:", hiddenWidgets.length > 0 ? hiddenWidgets.join(',') : 'none');
        
        return parsedData;
      } else {
        console.warn("getUserDashboards: Parsed data missing widgets array:", parsedData);
        return null;
      }
    } catch (error) {
      console.error("Error fetching user dashboards:", error);
      return null;
    }
  }

  public async registerTOTP(): Promise<string> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}auth/register_totp`, {
        method: "PUT",
        headers: this.headers,
      });

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) throw new Error("Unauthorized request");

      const data = await handledResponse.json();
      return data; // Assuming the response contains { totp_secret: string }
    } catch (error) {
      console.error("Error registering TOTP:", error);
      throw error;
    }
  }

  /**
   * Validates a TOTP and generates backup codes.
   * @param totp The 6-digit TOTP to validate.
   * @returns {Promise<{ backupCodes: string[] }>} The backup codes generated for the user.
   */
  // TODO: Check if this method is actually used - commented out for cleanup
  /*
  public async validateBackupCode(
    totp: string
  ): Promise<{ backupCodes: string[] }> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}auth/backup_codes`, {
        method: "GET",
        headers: this.headers,
      });

      if (!response.ok) {
        throw new Error("Failed to validate TOTP or generate backup codes.");
      }

      const data = await response.json();
      return {
        backupCodes: data.backup_codes || [],
      };
    } catch (error) {
      console.error("Error validating TOTP or generating backup codes:", error);
      throw error;
    }
  }
  */

  /**
   * Validates a TOTP token using the backend.
   * @param {string} totp - The TOTP token to validate.
   * @returns {Promise<{ valid: boolean }>} The validation result.
   */
  public async validateTOTP(
    totp: string
  ): Promise<{ isValid: boolean; backup_codes: any[] }> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}auth/backup_codes`, {
        method: "PUT",
        headers: this.headers,
        body: JSON.stringify({ totp }),
      });

      if (!response.ok) {
        throw new Error("Failed to validate TOTP token.");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error validating TOTP:", error);
      throw error;
    }
  }

  // TODO: Check if this method is actually used - commented out for cleanup
  /*
  public async validateAndGetBackupCodes(
    totp: string
  ): Promise<{ backupCodes: string[] }> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}auth/backup_codes`, {
        method: "GET",
        headers: this.headers,
        body: JSON.stringify({ totp }),
      });

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) throw new Error("Unauthorized request");

      const data = await handledResponse.json();
      if (!data.backup_codes) {
        throw new Error("Failed to fetch backup codes.");
      }

      return { backupCodes: data.backup_codes };
    } catch (error) {
      console.error("Error fetching backup codes:", error);
      throw error;
    }
  }
  */

  /**
   * Fetches the agency data from the API.
   * @returns {Promise<any>} The agency data.
   */
  public async getAgency(): Promise<any> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}agency`, {
        method: "GET",
        headers: this.headers,
      });

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) throw new Error("Unauthorized request");

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching agency data:", error);
      throw error;
    }
  }

  public async createAgency(agencyData: {
    agency_name: string;
    agency_creator: number;
    agency_token: string;
  }): Promise<any> {
    return this.apiPost<any>("agency", agencyData);
  }

  public async sendInviteEmail(email: string, name: string): Promise<string> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}auth/agent_invite`, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify({ name, email }),
      });

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) throw new Error("Unauthorized request");

      // No content expected from a successful invite email request
      return "Succesfully sent invite";
    } catch (error) {
      console.error(`Error sending invite email to ${email}:`, error);
      throw error;
    }
  }

  /**
   * Updates the agency details.
   * @param {string} name - The new name of the agency.
   * @returns {Promise<void>} Resolves when the agency name is updated.
   */
  public async updateAgencyDetails(
    agencyId: number,
    name: string,
    totpRequired: boolean
  ): Promise<void> {
    const agencyData = {
      agency_name: name,
      totp_required: totpRequired,
    };

    await this.apiPatch(`agency/${agencyId}`, agencyData);
  }
  /**
   * Fetches the user preferences for the current user from the API.
   * @returns {Promise<Object | false>} The user preferences if they exist, or false if not found or an error occurs.
   */
  public async getUserPreferences(): Promise<Object | false> {
    try {
      const userId = this.getUserId();
      if (!userId) throw new Error("User ID not found");

      // Check localStorage first for temporary preferences
      const localPreferences = localStorage.getItem(`preferences_${userId}`);
      if (localPreferences) {
        // console.log("🔍 Found preferences in localStorage:", localPreferences);
        return JSON.parse(localPreferences);
      }

      const response = await fetch(`${this.XANO_ENDPOINT}preferences`, {
        method: "GET",
        headers: this.headers,
      });

      if (!response.ok) {
        console.error("Failed to fetch preferences:", response.statusText);
        return false;
      }

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) throw new Error("Unauthorized request");

      const preferencesArray = await handledResponse.json();
      console.log("Fetched preferences array:", preferencesArray);

      // Check if preferencesArray is an array
      if (!Array.isArray(preferencesArray)) {
        console.error("Unexpected response format:", preferencesArray);
        return false;
      }

      console.log(
        "🔍 DEBUG: Looking for user_id:",
        userId,
        "type:",
        typeof userId
      );
      console.log(
        "🔍 DEBUG: Available preferences:",
        preferencesArray.map((p) => ({
          user_id: p.user_id,
          type: typeof p.user_id,
        }))
      );

      // More detailed debugging
      preferencesArray.forEach((pref, index) => {
        // console.log(`🔍 DEBUG: Preference ${index}:`, {
        //   user_id: pref.user_id,
        //   user_id_type: typeof pref.user_id,
        //   inflation: pref.inflation,
        //   annual_raise: pref.annual_raise,
        // });
      });

      const userPreferences = preferencesArray.find(
        (pref) => pref.user_id === userId
      );

      console.log("Found user preferences:", userPreferences);

      // Try with string comparison as well
      if (!userPreferences) {
        // console.log("🔍 DEBUG: Trying string comparison...");
        const userPreferencesString = preferencesArray.find(
          (pref) => pref.user_id === userId.toString()
        );
        console.log("Found with string comparison:", userPreferencesString);

        // Try with loose comparison
        if (!userPreferencesString) {
          // console.log("🔍 DEBUG: Trying loose comparison...");
          const userPreferencesLoose = preferencesArray.find(
            (pref) => pref.user_id == userId
          );
          console.log("Found with loose comparison:", userPreferencesLoose);

          // FIXED: If user_id is undefined, assume this is the user's preferences
          if (!userPreferencesLoose && preferencesArray.length === 1) {
            console.log(
              "🔍 DEBUG: user_id is undefined, assuming this is the user's preferences"
            );
            const pref = preferencesArray[0];
            // Add the user_id to the preferences object
            pref.user_id = userId;
            // console.log("🔍 DEBUG: Added user_id to preferences:", pref);
            return pref;
          }

          return userPreferencesLoose || false;
        }

        return userPreferencesString || false;
      }

      return userPreferences || false;
    } catch (error) {
      console.error("Error fetching user preferences:", error);
      return false;
    }
  }

  /**
   * Adds new user preferences to the API.
   * @param {Object} preferencesData - The data for the new user preferences.
   * @returns {Promise<Object>} The newly added user preferences data.
   */
  public async addPreferences(preferencesData: Object): Promise<Object> {
    return this.apiPost<Object>("preferences", preferencesData);
  }

  /**
   * Updates user preferences by ID.
   * @param {string} userId - The ID of the user.
   * @param {PlatformUserPreferences} preferencesData - The preference data to be updated.
   * @returns {Promise<Object | null>} The updated preferences data or null if not found.
   */
  public async updatePreferencesByUserId(
    userId: string,
    preferencesData: PlatformUserPreferences
  ): Promise<Object | null> {
    try {
      const preferencesArray = await this.apiGet<Array<any>>("preferences");

      if (!Array.isArray(preferencesArray)) {
        console.error("Unexpected response format:", preferencesArray);
        return null;
      }

      const userPreferences = preferencesArray.find(
        (pref) => parseInt(userId) === pref.user_id
      );

      if (!userPreferences) {
        console.log(
          "🔍 DEBUG: No preferences found with user_id, trying fallback logic"
        );

        // FIXED: Use the same fallback logic as getUserPreferences
        if (preferencesArray.length === 1) {
          console.log(
            "🔍 DEBUG: Found single preference with undefined user_id, using it"
          );
          const pref = preferencesArray[0];
          // console.log("🔍 DEBUG: Full preference object:", pref);
          // console.log("🔍 DEBUG: Preference object keys:", Object.keys(pref));
          // console.log("🔍 DEBUG: Preference ID field:", pref.id);

          // Check if the preference has an ID field
          if (!pref.id) {
            console.error("🔍 ERROR: Preference object has no 'id' field!");
            // console.log("🔍 Available fields:", Object.keys(pref));
            console.log(
              "🔍 Full preference object:",
              JSON.stringify(pref, null, 2)
            );

            // Try common ID field names
            const possibleIdFields = [
              "ID",
              "Id",
              "preference_id",
              "preferences_id",
              "record_id",
            ];
            for (const field of possibleIdFields) {
              if (pref[field]) {
                // console.log(`🔍 Found ID field '${field}':`, pref[field]);
                // Use this field as the ID
                pref.id = pref[field];
                break;
              }
            }

            // If still no ID found, we need to create a new preferences record
            if (!pref.id) {
              console.log(
                "🔍 No ID field found - preferences need database structure fix"
              );
              console.log(
                "🔍 Creating new preferences record with proper structure"
              );

              // Create a new preferences object with user_id and all the existing values
              const newPreferencesData = {
                user_id: parseInt(userId),
                inflation: preferencesData.inflation || pref.inflation,
                annual_raise: preferencesData.annual_raise || pref.annual_raise,
                before_retirement:
                  preferencesData.before_retirement || pref.before_retirement,
                during_retirement:
                  preferencesData.during_retirement || pref.during_retirement,
                payout_percent:
                  preferencesData.payout_percent || pref.payout_percent,
                federal_tsp_g:
                  preferencesData.federal_tsp_g || pref.federal_tsp_g,
                federal_tsp_f:
                  preferencesData.federal_tsp_f || pref.federal_tsp_f,
                federal_tsp_c:
                  preferencesData.federal_tsp_c || pref.federal_tsp_c,
                federal_tsp_s:
                  preferencesData.federal_tsp_s || pref.federal_tsp_s,
                federal_tsp_i:
                  preferencesData.federal_tsp_i || pref.federal_tsp_i,
              };

              console.log(
                "🔍 Creating new preferences with data:",
                newPreferencesData
              );

              // Try using the addPreferences method instead
              console.log(
                "🔍 Trying to add new preferences using addPreferences method"
              );

              try {
                const result = await this.addPreferences(newPreferencesData);
                // console.log("🔍 Successfully added new preferences:", result);
                return result;
              } catch (error) {
                console.log(
                  "🔍 POST endpoint not available (expected) - using localStorage fallback"
                );

                // Fallback: Store preferences in localStorage as temporary solution
                console.log(
                  "🔍 Fallback: storing preferences in localStorage as temporary solution"
                );

                // Store the updated preferences in localStorage
                const updatedPreferences = {
                  ...newPreferencesData,
                  id: `temp_${Date.now()}`, // Temporary ID
                  updated_at: new Date().toISOString(),
                };

                localStorage.setItem(
                  `preferences_${userId}`,
                  JSON.stringify(updatedPreferences)
                );
                console.log(
                  "🔍 Stored preferences in localStorage:",
                  updatedPreferences
                );
                console.log(
                  "🔍 Note: This is a temporary fix - preferences are stored locally until database is fixed"
                );

                // Return the updated data with a success indicator
                return {
                  ...updatedPreferences,
                  _temporary_update: true,
                  _message:
                    "Preferences updated and stored locally - database structure needs fixing",
                };
              }
            }
          }

          // Add the user_id to the preferences object for consistency
          pref.user_id = parseInt(userId);
          // console.log("🔍 DEBUG: Using preference with ID:", pref.id);

          console.log("Updating existing preferences with ID:", pref.id);
          console.log("Preferences data to update:", preferencesData);

          const result = await this.apiPut<Object>(
            `preferences/${pref.id}`,
            preferencesData
          );

          // console.log("🔍 DEBUG: PUT request result:", result);
          return result;
        }

        console.log(
          "No preferences found for the current user. Preferences should be created automatically when user is created."
        );
        console.log("User ID:", userId);
        console.log(
          "This might indicate a data issue - preferences should exist for all users."
        );
        return null;
      }

      // console.log("🔍 DEBUG: Found preferences with user_id match");
      // console.log("🔍 DEBUG: Full preference object:", userPreferences);
      console.log(
        "🔍 DEBUG: Preference object keys:",
        Object.keys(userPreferences)
      );
      // console.log("🔍 DEBUG: Preference ID field:", userPreferences.id);

      // Check if the preference has an ID field
      if (!userPreferences.id) {
        console.log(
          "🔍 No ID field found in preferences - database structure issue"
        );
        // console.log("🔍 Available fields:", Object.keys(userPreferences));
        return null;
      }

      console.log("Updating existing preferences with ID:", userPreferences.id);
      console.log("Preferences data to update:", preferencesData);

      const result = await this.apiPut<Object>(
        `preferences/${userPreferences.id}`,
        preferencesData
      );

      // console.log("🔍 DEBUG: PUT request result:", result);
      return result;
    } catch (error) {
      console.error("Error updating preferences:", error);
      return null;
    }
  }

  /**
   * Resets the user's password.
   * @param {string | number} userId - The ID of the user whose password is to be reset.
   * @param {string} newPassword - The new password for the user.
   * @returns {Promise<void>} A promise that resolves when the password has been reset.
   */
  public async resetPassword(
    userId: string | number,
    newPassword: string
  ): Promise<void> {
    await this.apiPut<void>(`user/${userId}/reset`, { password: newPassword });
    console.log(`Password reset successfully for user: ${userId}`);
  }

  /**
   * Generates a new agent report.
   * @param {number | string} clientId - The client ID for which to generate the report.
   * @returns {Promise<string>} The URL of the generated report.
   */
  public async generateAgentReport(clientId: number | string): Promise<string> {
    const data = await this.apiPost<any>("report_agent", {
      client_id: clientId,
    });
    return data?.response?.result?.data?.file_url;
  }

  public async getUsersByAgencyId(
    agencyId: number
  ): Promise<Array<TeamMember>> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}agency`, {
        method: "GET",
        headers: this.headers,
      });

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) throw new Error("Unauthorized request");

      const users = await response.json();
      // console.log(users, 888);
      return users;
    } catch (error) {
      console.error(`Error fetching users for agency ${agencyId}:`, error);
      throw error;
    }
  }

  public async addAgent(agentData: PlatformUser): Promise<PlatformUser> {
    return this.apiPost<PlatformUser>("agency", agentData);
  }

  public async getAgents(): Promise<Array<PlatformUser>> {
    return this.apiGet<Array<PlatformUser>>("agency");
  }

  public async removeAgent(roleId: string): Promise<void> {
    return this.apiDelete(`role/${roleId}`);
  }

  /**
   * Fetches permissions of a role by its ID.
   * @param {number} roleId - The ID of the role.
   * @returns {Promise<{ id: number, role: string, permissions: any[] }>} The role data including its permissions.
   */
  public async getRoleById(
    roleId: number
  ): Promise<{ id: number; role: string; permissions: any[] }> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}roles/${roleId}`, {
        method: "GET",
        headers: this.headers,
      });

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) throw new Error("Unauthorized request");

      const data = await handledResponse.json();
      return data;
    } catch (error) {
      console.error(`Error fetching role with ID ${roleId}:`, error);
      throw error;
    }
  }

  /**
   * Adds a new role to the currently authenticated user's agency.
   * @param {RoleType} roleData - The data for the new role.
   * @returns {Promise<RoleType>} The newly added role data.
   */
  public async addRole(roleData: RoleType): Promise<RoleType> {
    return this.apiPost<RoleType>("roles", roleData);
  }

  /**
   * Edits a role if the currently authenticated user is permitted to.
   * @param {number} roleId - The ID of the role to update.
   * @param {Partial<RoleType>} roleData - The role data to be updated.
   * @returns {Promise<RoleType>} The updated role data.
   */
  public async updateRole(
    roleId: number,
    roleData: Partial<RoleType>
  ): Promise<RoleType> {
    return this.apiPatch<RoleType>(`roles/${roleId}`, roleData);
  }

  /**
   * Fetches all knowledge base articles.
   * @returns {Promise<Array<KnowledgeBaseArticle> | null>} An array of knowledge base articles or null if an error occurs.
   */
  public async getAllKnowledgeBaseArticles(): Promise<Array<KnowledgeBaseArticle> | null> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}knowledge_base`, {
        method: "GET",
        headers: this.headers,
      });

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) throw new Error("Unauthorized request");

      const data = await handledResponse.json();
      return data;
    } catch (error) {
      console.error("Error fetching knowledge base articles:", error);
      return null;
    }
  }
  /**
   * Fetches a knowledge base article by its slug.
   * @param {string} slug - The slug of the knowledge base article.
   * @returns {Promise<KnowledgeBaseArticle | null>} The knowledge base article data or null if not found.
   */
  public async getKnowledgeBaseArticle(
    slug: string
  ): Promise<KnowledgeBaseArticle | null> {
    try {
      const response = await fetch(
        `${this.XANO_ENDPOINT}knowledge_base/${slug}`,
        {
          method: "GET",
          headers: this.headers,
        }
      );

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) throw new Error("Unauthorized request");

      const data = await handledResponse.json();
      return data;
    } catch (error) {
      console.error(
        `Error fetching knowledge base article with slug ${slug}:`,
        error
      );
      return null;
    }
  }

  public async getRoles(agency_id: number): Promise<RoleType[]> {
    try {
      const response = await fetch(
        `${this.XANO_ENDPOINT}agency/${agency_id}/roles`,
        {
          method: "GET",
          headers: this.headers,
        }
      );

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) throw new Error("Unauthorized request");

      const data = await handledResponse.json();
      return data;
    } catch (error) {
      console.error("Error fetching roles:", error);
      throw error;
    }
  }
  /**
   * Fetches a user's data by their ID.
   * @param {number} userId - The ID of the user.
   * @returns {Promise<PlatformUser>} The user's data.
   */
  public async getUserById(userId: number): Promise<PlatformUser> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}user/${userId}`, {
        method: "GET",
        headers: this.headers,
      });

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) throw new Error("Unauthorized request");

      const data = await handledResponse.json();
      return data;
    } catch (error) {
      console.error(`Error fetching user with ID ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Updates the user's data by their ID.
   * @param {number} userId - The user's ID.
   * @param {PlatformUser} user - The updated user object.
   * @returns {Promise<void>} Resolves when the user's data is updated.
   */
  public async updateUserStatus(
    userId: number,
    user: PlatformUser
  ): Promise<void> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}user/${userId}`, {
        method: "PUT",
        headers: this.headers,
        body: JSON.stringify(user),
      });

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) throw new Error("Unauthorized request");

      // No content expected from a successful update
    } catch (error) {
      console.error(`Error updating user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Generic GET request handler.
   * @param {string} endpoint - The API endpoint.
   * @param {Function} [processData] - Optional function to process the data.
   * @returns {Promise<any>} The response data.
   */
  private async apiGet<T>(
    endpoint: string,
    processData?: (data: T) => T
  ): Promise<T> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}${endpoint}`, {
        method: "GET",
        headers: this.headers,
      });

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) throw new Error("Unauthorized request");

      const data = await response.json();
      return processData ? processData(data) : data;
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * Fetches retirement income sources analytics from the private API
   * @returns {Promise<{sources: Array<{source: string, average: number}>, totalAverage: number} | null>}
   */
  // TODO: Uncomment when endpoint is available
  // Endpoint currently returns 404: analytics/retirement-income-sources
  /**
   * Fetches retirement income sources analytics from the private API
   * @param userId - Optional user ID filter (singular int)
   */
  public async getRetirementIncomeSources(userId?: number): Promise<{
    sources: Array<{ source: string; average: number }>;
    totalAverage: number;
  }> {
    const rawData = await this.fetchDashboardAnalytics<any>("dashboard/retirement-income-sources", userId);
    
    // console.log(`🔍 getRetirementIncomeSources: Raw data:`, rawData);
    // console.log(`🔍 getRetirementIncomeSources: Data type:`, typeof rawData);
    // console.log(`🔍 getRetirementIncomeSources: Data keys:`, rawData ? Object.keys(rawData) : 'null');
    
    if (!rawData) {
      // console.log(`⚠️ getRetirementIncomeSources: Endpoint returned null, returning empty structure`);
      return {
        sources: [],
        totalAverage: 0,
      };
    }
    
    // Handle wrapped response format: {success: true, data: {...}}
    const data = rawData.data || rawData;
    // console.log(`🔍 getRetirementIncomeSources: Extracted data:`, data);
    // console.log(`🔍 getRetirementIncomeSources: Data keys:`, Object.keys(data));
    // console.log(`🔍 getRetirementIncomeSources: Has sources?`, !!data.sources);
    // console.log(`🔍 getRetirementIncomeSources: Sources value:`, data.sources);
    // console.log(`🔍 getRetirementIncomeSources: Sources is array?`, Array.isArray(data.sources));
    // console.log(`🔍 getRetirementIncomeSources: Sources length:`, data.sources?.length);
    // console.log(`🔍 getRetirementIncomeSources: totalAverage:`, data.totalAverage);
    // console.log(`🔍 getRetirementIncomeSources: totalAverage type:`, typeof data.totalAverage);
    
    // Check if sources exists and is an array
    if (data.sources && Array.isArray(data.sources)) {
      // Filter out sources with null/undefined averages and ensure they have the correct structure
      const validSources = data.sources
        .filter((s: any) => s && s.source && (s.average !== null && s.average !== undefined))
        .map((s: any) => ({
          source: s.source,
          average: Number(s.average) || 0,
        }));
      
      // console.log(`🔍 getRetirementIncomeSources: Valid sources after filtering:`, validSources);
      // console.log(`🔍 getRetirementIncomeSources: Valid sources count:`, validSources.length);
      
      // Calculate totalAverage if not provided or if it's null
      const totalAverage = data.totalAverage !== null && data.totalAverage !== undefined
        ? Number(data.totalAverage) || 0
        : validSources.reduce((sum: number, s: { source: string; average: number }) => sum + s.average, 0);
      
      // console.log(`🔍 getRetirementIncomeSources: Calculated totalAverage:`, totalAverage);
      
      if (validSources.length > 0) {
        // console.log(`✅ getRetirementIncomeSources: Returning data with ${validSources.length} sources`);
        return {
          sources: validSources,
          totalAverage,
        };
      } else {
        console.log(`⚠️ getRetirementIncomeSources: No valid sources after filtering`);
      }
    }
    
    console.log(`⚠️ getRetirementIncomeSources: No sources found or empty, returning empty structure`);
    return {
      sources: [],
      totalAverage: 0,
    };
  }

  /**
   * Fetches AUM by product analytics from the private API
   * @returns {Promise<{products: Array<{product: string, amount: number, percentage: number}>, grandTotal: number} | null>}
   */
  /**
   * Fetches AUM by product analytics from the private API
   * @param userId - Optional user ID filter (singular int)
   */
  public async getAUMByProduct(userId?: number): Promise<{
    products: Array<{ product: "tsp" | "401k" | "403b" | "annuity"; amount: number; percentage: number }>;
    grandTotal: number;
  } | null> {
    try {
      if (!this.isAuthenticated()) {
        throw new Error("User must be authenticated to fetch analytics");
      }

      // Build URL with optional user_id query parameter
      let endpoint = `${this.XANO_PRIVATE_ENDPOINT}dashboard/aum-by-product`;
      if (userId !== undefined && userId !== null) {
        endpoint = `${endpoint}?user_id=${userId}`;
      }
      const response = await fetch(endpoint, {
        method: "GET",
        headers: this.headers,
      });

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) {
        throw new Error("Unauthorized request");
      }

      if (!handledResponse.ok) {
        const errorText = await handledResponse.text();
        let errorMessage = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.code || errorText;
        } catch {
          // If parsing fails, use the raw error text
        }
        console.error(`❌ getAUMByProduct: Error fetching AUM by product (${handledResponse.status}):`, errorMessage);
        console.error(`❌ getAUMByProduct: Full error response:`, errorText);
        
        // Return empty structure instead of null for better error handling
        return {
          products: [],
          grandTotal: 0,
        };
      }

      const rawData = await handledResponse.json();
      
      // Handle wrapped response format: {success: true, data: {...}}
      const data = rawData.data || rawData;
      
      // Log the response for debugging
      console.log("🔍 getAUMByProduct: Raw response:", rawData);
      console.log("🔍 getAUMByProduct: Extracted data:", data);
      console.log("🔍 getAUMByProduct: Has products?", !!data?.products);
      console.log("🔍 getAUMByProduct: Products count:", data?.products?.length);
      console.log("🔍 getAUMByProduct: Grand total:", data?.grandTotal);
      
      // Always return data structure, even if empty - let the widget handle empty state
      if (!data) {
        console.warn("⚠️ getAUMByProduct: No data in response, returning empty structure");
        return {
          products: [],
          grandTotal: 0,
        };
      }

      // Ensure products is an array
      const products = Array.isArray(data.products) ? data.products : [];
      const grandTotal = typeof data.grandTotal === 'number' ? data.grandTotal : 0;
      
      // Log if we're returning empty data
      if (products.length === 0 || grandTotal === 0) {
        console.log("⚠️ getAUMByProduct: Returning empty data structure (no products or zero grandTotal)");
      }
      
      // Return data even if products array is empty or grandTotal is 0
      // This allows the widget to display "No AUM data available" message
      return {
        products: products.map((p: any) => ({
          product: p.product || "",
          amount: typeof p.amount === 'number' ? p.amount : 0,
          percentage: typeof p.percentage === 'number' ? p.percentage : 0,
        })),
        grandTotal: grandTotal,
      };
    } catch (error) {
      console.error("❌ getAUMByProduct: Exception fetching AUM by product:", error);
      // Return empty structure instead of null for better error handling
      return {
        products: [],
        grandTotal: 0,
      };
    }
  }

  /**
   * Generic helper to fetch dashboard analytics from private API
   * @param endpoint - The endpoint path (e.g., "dashboard/retirement-readiness")
   * @param userId - Optional user ID filter (singular int)
   */
  private async fetchDashboardAnalytics<T>(endpoint: string, userId?: number): Promise<T | null> {
    try {
      if (!this.isAuthenticated()) {
        throw new Error("User must be authenticated to fetch analytics");
      }

      // Build URL with optional user_id query parameter
      let fullEndpoint = `${this.XANO_PRIVATE_ENDPOINT}${endpoint}`;
      if (userId !== undefined && userId !== null) {
        const separator = endpoint.includes('?') ? '&' : '?';
        fullEndpoint = `${fullEndpoint}${separator}user_id=${userId}`;
      }
      // console.log(`🔍 fetchDashboardAnalytics: Fetching from ${fullEndpoint}`);

      const response = await fetch(fullEndpoint, {
        method: "GET",
        headers: this.headers,
      });

      // console.log(`🔍 fetchDashboardAnalytics: Response status for ${endpoint}:`, response.status, response.statusText);
      // console.log(`🔍 fetchDashboardAnalytics: Response ok:`, response.ok);

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) {
        console.error(`❌ fetchDashboardAnalytics: Unauthorized request for ${endpoint}`);
        throw new Error("Unauthorized request");
      }

      if (!handledResponse.ok) {
        const errorText = await handledResponse.text();
        console.error(`❌ fetchDashboardAnalytics: Error fetching ${endpoint}:`, errorText);
        console.error(`❌ fetchDashboardAnalytics: Status:`, handledResponse.status);
        return null;
      }

      const data = await handledResponse.json();
      // console.log(`🔍 fetchDashboardAnalytics: Raw data from ${endpoint}:`, data);
      // console.log(`🔍 fetchDashboardAnalytics: Data type:`, typeof data);
      // console.log(`🔍 fetchDashboardAnalytics: Data keys:`, data ? Object.keys(data) : 'null/undefined');
      
      // Return data even if it's empty - let the calling method decide what to do with it
      return data || null;
    } catch (error) {
      console.error(`❌ fetchDashboardAnalytics: Exception fetching ${endpoint}:`, error);
      return null;
    }
  }

  /**
   * Fetches retirement readiness analytics from the private API
   * @param userId - Optional user ID filter (singular int)
   */
  public async getRetirementReadiness(userId?: number): Promise<{
    buckets: {
      fullyFunded: { count: number; percentage: number; avgRatio: number };
      minorGap: { count: number; percentage: number; avgRatio: number };
      significantGap: { count: number; percentage: number; avgRatio: number };
    };
    totalClients: number;
  } | null> {
    const rawData = await this.fetchDashboardAnalytics<any>("dashboard/retirement-readiness", userId);
    
    // console.log(`🔍 getRetirementReadiness: Raw data:`, rawData);
    // console.log(`🔍 getRetirementReadiness: Data type:`, typeof rawData);
    // console.log(`🔍 getRetirementReadiness: Data keys:`, rawData ? Object.keys(rawData) : 'null');
    
    if (!rawData) {
      console.log(`⚠️ getRetirementReadiness: Endpoint returned null`);
      return null;
    }
    
    // Handle wrapped response format: {success: true, data: {...}}
    const data = rawData.data || rawData;
    // console.log(`🔍 getRetirementReadiness: Extracted data:`, data);
    // console.log(`🔍 getRetirementReadiness: Data keys:`, Object.keys(data));
    // console.log(`🔍 getRetirementReadiness: Has buckets?`, !!data.buckets);
    // console.log(`🔍 getRetirementReadiness: Buckets value:`, data.buckets);
    // console.log(`🔍 getRetirementReadiness: Buckets is array?`, Array.isArray(data.buckets));
    // console.log(`🔍 getRetirementReadiness: totalClients:`, data.totalClients);
    // console.log(`🔍 getRetirementReadiness: totalClients type:`, typeof data.totalClients);
    
    if (!data.buckets) {
      console.log(`⚠️ getRetirementReadiness: No buckets found in response`);
      // console.log(`🔍 getRetirementReadiness: Full data structure:`, JSON.stringify(data, null, 2));
      return null;
    }
    
    // Handle array format: buckets is an array of objects
    if (Array.isArray(data.buckets)) {
      // console.log(`🔍 getRetirementReadiness: Buckets is an array, mapping to object structure`);
      // console.log(`🔍 getRetirementReadiness: Buckets array:`, JSON.stringify(data.buckets, null, 2));
      
      // Initialize buckets object
      const bucketsObj: {
        fullyFunded: { count: number; percentage: number; avgRatio: number };
        minorGap: { count: number; percentage: number; avgRatio: number };
        significantGap: { count: number; percentage: number; avgRatio: number };
      } = {
        fullyFunded: { count: 0, percentage: 0, avgRatio: 0 },
        minorGap: { count: 0, percentage: 0, avgRatio: 0 },
        significantGap: { count: 0, percentage: 0, avgRatio: 0 },
      };
      
      // Map array items to object structure
      data.buckets.forEach((bucket: any) => {
        // Check for label first (API format), then category, name, or key
        const category = bucket.label || bucket.category || bucket.name || bucket.key;
        const count = bucket.count || 0;
        const percentage = bucket.percentage || 0;
        const avgRatio = bucket.avgRatio || bucket.averageRatio || bucket.ratio || 0;
        
        // console.log(`🔍 getRetirementReadiness: Processing bucket - label: ${bucket.label}, category: ${category}, count: ${count}, percentage: ${percentage}, avgRatio: ${avgRatio}`);
        
        // Match by label (exact API format) or various category name formats
        const labelLower = category?.toLowerCase() || '';
        if (labelLower === 'fully funded' || category === 'fullyFunded' || category === 'fully_funded') {
          bucketsObj.fullyFunded = { count, percentage, avgRatio };
          console.log(`✅ Mapped to fullyFunded: count=${count}, percentage=${percentage}, avgRatio=${avgRatio}`);
        } else if (labelLower === 'minor gap' || category === 'minorGap' || category === 'minor_gap') {
          bucketsObj.minorGap = { count, percentage, avgRatio };
          console.log(`✅ Mapped to minorGap: count=${count}, percentage=${percentage}, avgRatio=${avgRatio}`);
        } else if (labelLower === 'significant gap' || category === 'significantGap' || category === 'significant_gap') {
          bucketsObj.significantGap = { count, percentage, avgRatio };
          console.log(`✅ Mapped to significantGap: count=${count}, percentage=${percentage}, avgRatio=${avgRatio}`);
        } else {
          console.log(`⚠️ Unknown bucket category: ${category}, skipping`);
        }
      });
      
      console.log(`✅ getRetirementReadiness: Mapped buckets object:`, JSON.stringify(bucketsObj, null, 2));
      return {
        buckets: bucketsObj,
        totalClients: data.totalClients || 0,
      };
    }
    
    // Handle object format: buckets is already an object
    if (typeof data.buckets === 'object' && !Array.isArray(data.buckets)) {
      console.log(`✅ getRetirementReadiness: Buckets is already an object, returning as-is`);
      // console.log(`🔍 getRetirementReadiness: Buckets structure:`, JSON.stringify(data.buckets, null, 2));
      return {
        buckets: data.buckets,
        totalClients: data.totalClients || 0,
      };
    }
    
    // If buckets format is unexpected, return null
    console.log(`⚠️ getRetirementReadiness: Unexpected buckets format`);
    // console.log(`🔍 getRetirementReadiness: Full data structure:`, JSON.stringify(data, null, 2));
    return null;
  }

  /**
   * Fetches contribution rate distribution analytics from the private API
   * @param userId - Optional user ID filter (singular int)
   */
  public async getContributionRate(userId?: number): Promise<{
    bands: {
      under3: number;
      "3to5": number;
      "5to10": number;
      "10to15": number;
      over15: number;
    };
  } | null> {
    const rawData = await this.fetchDashboardAnalytics<any>("dashboard/contribution-rate", userId);
    
    // console.log(`🔍 getContributionRate: Raw data:`, rawData);
    
    if (!rawData) {
      console.log(`❌ getContributionRate: No data returned`);
      return null;
    }
    
    // Handle wrapped response format: {success: true, data: {...}}
    const data = rawData.data || rawData;
    
    // Check if data already has bands property (expected format)
    if (data.bands) {
      console.log(`✅ getContributionRate: Returning data with bands`);
      return { bands: data.bands };
    }
    
    // API returns: {0-5%: 245, 6-10%: 0, 11-15%: 0, 15%+: 0}
    // We need to map to: {bands: {under3, 3to5, 5to10, 10to15, over15}}
    // Note: API bands overlap with our bands, so we map the closest match
    const bands = {
      under3: data["under3"] || data["0-3%"] || 0, // No direct match in API, default to 0
      "3to5": data["0-5%"] || data["3to5"] || data["3-5%"] || 0, // Map 0-5% to 3-5%
      "5to10": data["6-10%"] || data["5to10"] || data["5-10%"] || 0, // Map 6-10% to 5-10%
      "10to15": data["11-15%"] || data["10to15"] || data["10-15%"] || 0, // Map 11-15% to 10-15%
      over15: data["15%+"] || data["over15"] || data["15+"] || 0, // Map 15%+ to over15
    };
    
    console.log(`✅ getContributionRate: Mapped bands:`, bands);
    return { bands };
  }

  /**
   * Fetches social security claiming analytics from the private API
   */
  /**
   * Fetches social security analytics from the private API
   * @param userId - Optional user ID filter (singular int)
   */
  public async getSocialSecurity(userId?: number): Promise<{
    categories: {
      "62": number;
      FRA: number;
      "70": number;
      unknown: number;
    };
  }> {
    const rawData = await this.fetchDashboardAnalytics<any>("dashboard/social-security", userId);
    
    // console.log(`🔍 getSocialSecurity: Raw data:`, rawData);
    // console.log(`🔍 getSocialSecurity: Raw data type:`, typeof rawData);
    
    if (!rawData) {
      console.log(`⚠️ getSocialSecurity: No data returned, returning empty structure`);
      return {
        categories: {
          "62": 0,
          FRA: 0,
          "70": 0,
          unknown: 0,
        },
      };
    }
    
    // Handle wrapped response format: {success: true, data: {...}}
    const data = rawData.data || rawData;
    // console.log(`🔍 getSocialSecurity: Extracted data:`, data);
    // console.log(`🔍 getSocialSecurity: Data keys:`, Object.keys(data));
    // console.log(`🔍 getSocialSecurity: Has categories?`, !!data.categories);
    // console.log(`🔍 getSocialSecurity: Categories value:`, data.categories);
    // console.log(`🔍 getSocialSecurity: Categories type:`, typeof data.categories);
    
    // Log detailed category structure
    if (data.categories) {
      // console.log(`🔍 getSocialSecurity: Categories keys:`, Object.keys(data.categories));
      // console.log(`🔍 getSocialSecurity: Categories entries:`, Object.entries(data.categories));
      Object.entries(data.categories).forEach(([key, value]) => {
        // console.log(`🔍 getSocialSecurity: Category "${key}": ${value} (type: ${typeof value})`);
      });
    }
    
    // Check if categories already exists and has keys
    if (data.categories && typeof data.categories === 'object' && Object.keys(data.categories).length > 0) {
      console.log(`✅ getSocialSecurity: Returning data with categories`);
      // console.log(`🔍 getSocialSecurity: Categories structure:`, JSON.stringify(data.categories, null, 2));
      // console.log(`🔍 getSocialSecurity: Categories has ${Object.keys(data.categories).length} keys`);
      
      // Normalize categories to ensure all required keys exist
      // Use nullish coalescing (??) instead of || to preserve 0 values
      const categories: {
        "62": number;
        FRA: number;
        "70": number;
        unknown: number;
      } = {
        "62": (data.categories["62"] ?? data.categories[62] ?? data.categories["Claim at 62"] ?? 0) as number,
        FRA: (data.categories.FRA ?? data.categories.fra ?? data.categories["Claim at FRA"] ?? 0) as number,
        "70": (data.categories["70"] ?? data.categories[70] ?? data.categories["Claim at 70"] ?? 0) as number,
        unknown: (data.categories.unknown ?? data.categories.Unknown ?? data.categories["Unknown"] ?? 0) as number,
      };
      
      // console.log(`🔍 getSocialSecurity: Normalized categories:`, JSON.stringify(categories, null, 2));
      // console.log(`🔍 getSocialSecurity: Category values - 62: ${categories["62"]}, FRA: ${categories.FRA}, 70: ${categories["70"]}, unknown: ${categories.unknown}`);
      return { categories };
    }
    
    // If no categories or empty categories, return empty structure
    console.log(`⚠️ getSocialSecurity: No categories found or empty, returning empty structure`);
    if (data.categories && typeof data.categories === 'object' && Object.keys(data.categories).length === 0) {
      // console.log(`🔍 getSocialSecurity: Categories object exists but is empty`);
    }
    return {
      categories: {
        "62": 0,
        FRA: 0,
        "70": 0,
        unknown: 0,
      },
    };
  }

  /**
   * Fetches FEGLI risk analytics from the private API
   * @param userId - Optional user ID filter (singular int)
   */
  public async getFEGLIRisk(userId?: number): Promise<{
    categories: {
      low: number;
      moderate: number;
      high: number;
    };
  }> {
    const rawData = await this.fetchDashboardAnalytics<any>("dashboard/fegli-risk", userId);
    
    // console.log(`🔍 getFEGLIRisk: Raw data:`, rawData);
    // console.log(`🔍 getFEGLIRisk: Data type:`, typeof rawData);
    // console.log(`🔍 getFEGLIRisk: Data keys:`, rawData ? Object.keys(rawData) : 'null');
    
    if (!rawData) {
      console.log(`⚠️ getFEGLIRisk: Endpoint returned null, returning empty structure`);
      return {
        categories: {
          low: 0,
          moderate: 0,
          high: 0,
        },
      };
    }
    
    // API returns: {low_risk: 0, moderate_risk: 0, high_risk: 0, total_analyzed: 0}
    // We need to map to: {categories: {low, moderate, high}}
    // Use nullish coalescing (??) to preserve 0 values
    const categories = {
      low: rawData.low_risk ?? rawData.low ?? 0,
      moderate: rawData.moderate_risk ?? rawData.moderate ?? 0,
      high: rawData.high_risk ?? rawData.high ?? 0,
    };
    
    // console.log(`🔍 getFEGLIRisk: Raw values - low_risk: ${rawData.low_risk}, moderate_risk: ${rawData.moderate_risk}, high_risk: ${rawData.high_risk}`);
    // console.log(`🔍 getFEGLIRisk: total_analyzed: ${rawData.total_analyzed}`);
    console.log(`✅ getFEGLIRisk: Mapped categories:`, JSON.stringify(categories, null, 2));
    return { categories };
  }

  /**
   * Fetches client age distribution analytics from the private API
   * @param userId - Optional user ID filter (singular int)
   */
  public async getClientAgeDistribution(userId?: number): Promise<{
    buckets: {
      under40: number;
      "40to49": number;
      "50to59": number;
      "60to69": number;
      "70plus": number;
    };
  } | null> {
    const rawData = await this.fetchDashboardAnalytics<any>("dashboard/age-distribution", userId);
    
    // console.log(`🔍 getClientAgeDistribution: Raw data:`, rawData);
    // console.log(`🔍 getClientAgeDistribution: Data type:`, typeof rawData);
    
    if (!rawData) {
      console.log(`⚠️ getClientAgeDistribution: Endpoint returned null`);
      return null;
    }
    
    // Handle wrapped response format: {success: true, data: {...}}
    const data = rawData.data || rawData;
    
    // Check if buckets exists
    if (data.buckets) {
      console.log(`✅ getClientAgeDistribution: Returning data with buckets`);
      return {
        buckets: data.buckets,
      };
    }
    
    console.log(`⚠️ getClientAgeDistribution: No buckets found`);
    return null;
  }

  /**
   * Fetches rollover opportunity analytics from the private API
   * @param userId - Optional user ID filter (singular int)
   */
  public async getRolloverOpportunity(userId?: number): Promise<{
    opportunities: Array<{
      clientId: number | string;
      clientName: string;
      rolloverValue: number;
    }>;
  } | null> {
    const rawData = await this.fetchDashboardAnalytics<any>("dashboard/rollover-opportunity", userId);
    
    console.log(`🔍 getRolloverOpportunity: Raw data:`, rawData);
    
    if (!rawData) {
      return null;
    }
    
    // Handle wrapped response format: {success: true, data: {...}}
    const data = rawData.data || rawData;
    
    console.log(`🔍 getRolloverOpportunity: Extracted data:`, data);
    
    // Check if opportunities already exists
    if (data.opportunities && Array.isArray(data.opportunities) && data.opportunities.length > 0) {
      // Log first few opportunities to verify data quality
      console.log(`🔍 getRolloverOpportunity: First 3 opportunities:`, JSON.stringify(data.opportunities.slice(0, 3), null, 2));
      
      // Check for suspicious patterns
      const uniqueNames = new Set(data.opportunities.map((o: any) => o.clientName));
      const uniqueValues = new Set(data.opportunities.map((o: any) => o.rolloverValue));
      if (uniqueNames.size <= 2 || uniqueValues.size <= 2) {
        console.warn(`⚠️ getRolloverOpportunity: Suspicious API data - only ${uniqueNames.size} unique names and ${uniqueValues.size} unique values`);
      }
      
      console.log(`✅ getRolloverOpportunity: Returning data with ${data.opportunities.length} opportunities`);
      return { opportunities: data.opportunities };
    }
    
    // If no opportunities or empty array, return null
    console.log(`⚠️ getRolloverOpportunity: No opportunities found`);
    return null;
  }

  /**
   * Fetches employer match utilization analytics from the private API
   */
  /**
   * Fetches employer match analytics from the private API
   * @param userId - Optional user ID filter (singular int)
   */
  public async getEmployerMatch(userId?: number): Promise<{
    categories: {
      noMatch: number;
      underMatch: number;
      atOrAboveMatch: number;
    };
  }> {
    const rawData = await this.fetchDashboardAnalytics<any>("dashboard/employer-match", userId);
    
    // console.log(`🔍 getEmployerMatch: Raw data:`, rawData);
    // console.log(`🔍 getEmployerMatch: Data type:`, typeof rawData);
    // console.log(`🔍 getEmployerMatch: Data keys:`, rawData ? Object.keys(rawData) : 'null');
    
    if (!rawData) {
      console.log(`⚠️ getEmployerMatch: Endpoint returned null, returning empty structure`);
      return {
        categories: {
          noMatch: 0,
          underMatch: 0,
          atOrAboveMatch: 0,
        },
      };
    }
    
    // Handle wrapped response format: {success: true, data: {...}}
    const data = rawData.data || rawData;
    // console.log(`🔍 getEmployerMatch: Extracted data:`, data);
    // console.log(`🔍 getEmployerMatch: Data keys:`, Object.keys(data));
    // console.log(`🔍 getEmployerMatch: Has categories?`, !!data.categories);
    // console.log(`🔍 getEmployerMatch: Categories value:`, data.categories);
    // console.log(`🔍 getEmployerMatch: Categories type:`, typeof data.categories);
    
    // Log detailed category structure
    if (data.categories) {
      // console.log(`🔍 getEmployerMatch: Categories keys:`, Object.keys(data.categories));
      // console.log(`🔍 getEmployerMatch: Categories entries:`, Object.entries(data.categories));
      Object.entries(data.categories).forEach(([key, value]) => {
        // console.log(`🔍 getEmployerMatch: Category "${key}": ${value} (type: ${typeof value})`);
      });
    }
    
    // Check if categories already exists and has keys
    if (data.categories && typeof data.categories === 'object' && Object.keys(data.categories).length > 0) {
      console.log(`✅ getEmployerMatch: Returning data with categories`);
      // console.log(`🔍 getEmployerMatch: Categories structure:`, JSON.stringify(data.categories, null, 2));
      // console.log(`🔍 getEmployerMatch: Categories has ${Object.keys(data.categories).length} keys`);
      
      // Ensure all required keys exist (use nullish coalescing to preserve 0 values)
      const categories = {
        noMatch: data.categories.noMatch ?? data.categories.no_match ?? data.categories["No Match"] ?? 0,
        underMatch: data.categories.underMatch ?? data.categories.under_match ?? data.categories["Under Match"] ?? 0,
        atOrAboveMatch: data.categories.atOrAboveMatch ?? data.categories.at_or_above_match ?? data.categories["At/Above Match"] ?? 0,
      };
      
      // console.log(`🔍 getEmployerMatch: Normalized categories:`, JSON.stringify(categories, null, 2));
      // console.log(`🔍 getEmployerMatch: Normalized values - noMatch: ${categories.noMatch}, underMatch: ${categories.underMatch}, atOrAboveMatch: ${categories.atOrAboveMatch}`);
      return { categories };
    }
    
    // If no categories or empty categories, return empty structure
    console.log(`⚠️ getEmployerMatch: No categories found or empty, returning empty structure`);
    if (data.categories && typeof data.categories === 'object' && Object.keys(data.categories).length === 0) {
      // console.log(`🔍 getEmployerMatch: Categories object exists but is empty`);
    }
    return {
      categories: {
        noMatch: 0,
        underMatch: 0,
        atOrAboveMatch: 0,
      },
    };
  }

  /**
   * Fetches retirement plan type spread analytics from the private API
   * @returns {Promise<{categories: Record<string, number>, fersSubtypes?: {FERS: {[key: string]: number}}} | null>}
   */
  /**
   * Fetches plan type spread analytics from the private API
   * @param userId - Optional user ID filter (singular int)
   */
  public async getPlanTypeSpread(userId?: number): Promise<{
    categories: {
      FERS?: number;
      CSRS?: number;
      "401k"?: number;
      "403b"?: number;
      Private?: number;
      NoPlan?: number;
      Unknown?: number;
    };
    fersSubtypes?: {
      FERS: {
        "FERS Basic"?: number;
        "FERS-RAE"?: number;
        "FERS-FRAE"?: number;
      };
    };
  }> {
    const rawData = await this.fetchDashboardAnalytics<any>("dashboard/plan-type-spread", userId);
    
    // console.log(`🔍 getPlanTypeSpread: Raw data:`, rawData);
    // console.log(`🔍 getPlanTypeSpread: Data type:`, typeof rawData);
    // console.log(`🔍 getPlanTypeSpread: Data keys:`, rawData ? Object.keys(rawData) : 'null');
    
    if (!rawData) {
      console.log(`⚠️ getPlanTypeSpread: Endpoint returned null, returning empty structure`);
      return {
        categories: {},
      };
    }
    
    // Handle wrapped response format: {success: true, data: {...}}
    const data = rawData.data || rawData;
    // console.log(`🔍 getPlanTypeSpread: Extracted data:`, data);
    // console.log(`🔍 getPlanTypeSpread: Data keys:`, Object.keys(data));
    // console.log(`🔍 getPlanTypeSpread: Has categories?`, !!data.categories);
    // console.log(`🔍 getPlanTypeSpread: Categories value:`, data.categories);
    // console.log(`🔍 getPlanTypeSpread: Categories type:`, typeof data.categories);
    // console.log(`🔍 getPlanTypeSpread: Has fersSubtypes?`, !!data.fersSubtypes);
    // console.log(`🔍 getPlanTypeSpread: fersSubtypes value:`, data.fersSubtypes);
    
    // Log detailed category structure
    if (data.categories) {
      // console.log(`🔍 getPlanTypeSpread: Categories keys:`, Object.keys(data.categories));
      // console.log(`🔍 getPlanTypeSpread: Categories entries:`, Object.entries(data.categories));
      Object.entries(data.categories).forEach(([key, value]) => {
        // console.log(`🔍 getPlanTypeSpread: Category "${key}": ${value} (type: ${typeof value})`);
      });
    }
    
    // Log detailed fersSubtypes structure
    if (data.fersSubtypes) {
      // console.log(`🔍 getPlanTypeSpread: fersSubtypes keys:`, Object.keys(data.fersSubtypes));
      // console.log(`🔍 getPlanTypeSpread: fersSubtypes structure:`, JSON.stringify(data.fersSubtypes, null, 2));
    }
    
    // Check if categories already exists and has keys
    if (data.categories && typeof data.categories === 'object' && Object.keys(data.categories).length > 0) {
      console.log(`✅ getPlanTypeSpread: Returning data with categories`);
      // console.log(`🔍 getPlanTypeSpread: Categories structure:`, JSON.stringify(data.categories, null, 2));
      // console.log(`🔍 getPlanTypeSpread: Categories has ${Object.keys(data.categories).length} keys`);
      return {
        categories: data.categories,
        fersSubtypes: data.fersSubtypes,
      };
    }
    
    // If no categories or empty categories, return empty structure with expected keys
    console.log(`⚠️ getPlanTypeSpread: No categories found or empty, returning empty structure`);
    if (data.categories && typeof data.categories === 'object' && Object.keys(data.categories).length === 0) {
      // console.log(`🔍 getPlanTypeSpread: Categories object exists but is empty`);
    }
    return {
      categories: {},
    };
  }

  /**
   * Fetches longevity stress test analytics from the private API
   * @param userId - Optional user ID filter (singular int)
   * @returns {Promise<{atRiskCount: number, fundedCount: number, avgYearsShort: number, totalClients: number} | null>}
   */
  public async getLongevityStress(userId?: number): Promise<{
    atRiskCount: number;
    fundedCount: number;
    avgYearsShort: number;
    totalClients: number;
  } | null> {
    const rawData = await this.fetchDashboardAnalytics<any>("dashboard/longevity-stress", userId);
    
    // console.log(`🔍 getLongevityStress: Raw data:`, rawData);
    
    if (!rawData) {
      return null;
    }
    
    // API returns: {atRisk: 0, funded: 245, avgYearsShort: 0, totalClients: 245}
    // We need to map to: {atRiskCount, fundedCount, avgYearsShort, totalClients}
    const result = {
      atRiskCount: rawData.atRisk || rawData.atRiskCount || 0,
      fundedCount: rawData.funded || rawData.fundedCount || 0,
      avgYearsShort: rawData.avgYearsShort || 0,
      totalClients: rawData.totalClients || 0,
    };
    
    console.log(`✅ getLongevityStress: Mapped result:`, result);
    return result;
  }

  /**
   * Fetches location insights analytics from the private API
   * @param userId - Optional user ID filter (singular int)
   * @returns {Promise<{locations: Array<{state: string, count: number, avgIncome: number, avgReadiness: number}>} | null>}
   */
  public async getLocationInsights(userId?: number): Promise<{
    locations: Array<{
      state: string;
      count: number;
      avgIncome: number;
      avgReadiness: number;
    }>;
  } | null> {
    const rawData = await this.fetchDashboardAnalytics<any>("dashboard/location-insights", userId);
    
    // console.log(`🔍 getLocationInsights: Raw data:`, rawData);
    // console.log(`🔍 getLocationInsights: Raw data type:`, typeof rawData);
    // console.log(`🔍 getLocationInsights: Raw data keys:`, rawData ? Object.keys(rawData) : 'null/undefined');
    
    if (!rawData) {
      console.log(`⚠️ getLocationInsights: No raw data returned from API`);
      return null;
    }
    
    // Handle wrapped response format: {success: true, locations: [...]} or {data: {locations: [...]}}
    // API returns locations directly or wrapped in data
    const locations = rawData.locations || rawData.data?.locations || (Array.isArray(rawData) ? rawData : []);
    
    // console.log(`🔍 getLocationInsights: Extracted locations:`, locations);
    // console.log(`🔍 getLocationInsights: Locations is array?:`, Array.isArray(locations));
    // console.log(`🔍 getLocationInsights: Locations length:`, Array.isArray(locations) ? locations.length : 0);
    
    if (!Array.isArray(locations) || locations.length === 0) {
      console.log(`⚠️ getLocationInsights: No locations found in response`);
      // console.log(`🔍 getLocationInsights: Full raw data structure:`, JSON.stringify(rawData, null, 2));
      return null;
    }
    
    console.log(`✅ getLocationInsights: Returning data with ${locations.length} locations`);
    
    // Log detailed information about the API response
    if (locations.length > 0) {
      const totalClients = locations.reduce((sum: number, loc: any) => sum + (loc.count || 0), 0);
      console.log(`🗺️ getLocationInsights: Total clients across all locations: ${totalClients}`);
      console.log(`🗺️ getLocationInsights: First location:`, JSON.stringify(locations[0], null, 2));
      console.log(`🗺️ getLocationInsights: All locations with counts:`, locations.map((loc: any) => ({
        name: loc.name,
        state: loc.state,
        count: loc.count
      })));
      
      // Check if this looks like sample data (low counts, few states)
      if (locations.length < 10 || totalClients < 50) {
        console.warn(`⚠️ getLocationInsights: WARNING - This might be sample data! Only ${locations.length} states with ${totalClients} total clients`);
      }
    }
    // console.log(`🔍 getLocationInsights: Sample locations:`, locations.slice(0, 3));
    return { locations };
  }

  public async getBillingCards(): Promise<Array<CardType>> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}billing/cards`, {
        method: "GET",
        headers: this.headers,
      });

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) throw new Error("Unauthorized request");

      const data = await handledResponse.json();
      return data;
    } catch (error) {
      console.error("Error fetching billing cards:", error);
      throw error;
    }
  }

  /**
   * Fetches billing invoices from the API.
   * @returns {Promise<InvoiceType[]>} An array of invoices.
   */
  public async getBillingInvoices(): Promise<InvoiceType[]> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}billing/invoices`, {
        method: "GET",
        headers: this.headers,
      });
      const handledResponse = this.handleResponse(response);
      if (!handledResponse) throw new Error("Unauthorized request");
      return await handledResponse.json();
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
      throw error;
    }
  }

  /**
   * Sets a card as the default payment method.
   * @param cardId The ID of the card to make default.
   * @returns {Promise<void>}
   */
  public async addBillingCard(
    cardId: string
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}billing/cards`, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify({ token: cardId }),
      });

      if (!response.ok) {
        // Optionally, try to get more details from the response
        const errorMessage = await response.text();
        throw new Error(errorMessage || "Failed to set default card");
      }

      // Optionally, log or handle any additional information on success
      console.log(`Card with ID ${cardId} set as default.`);
      return { success: true }; // Return success as true on a successful request
    } catch (error) {
      console.error("Error making card default:", error);

      // Return an object with success as false and the error message
      return {
        success: false,
        message: "An error occurred while setting the default card.",
      };
    }
  }

  public async deleteBillingCard(cardId: string): Promise<void> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}billing/cards`, {
        method: "DELETE",
        headers: this.headers,
        body: JSON.stringify({
          token: cardId,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to delete card. Please try again.");
      }

      console.log(`Card ${cardId} deleted successfully.`);
    } catch (error) {
      console.error("Error deleting card:", error);
      throw error; // Rethrow the error to handle it in the UI
    }
  }

  /**
   * Generic POST request handler.
   * @param {string} endpoint - The API endpoint.
   * @param {Object} bodyData - The request body data.
   * @returns {Promise<any>} The response data.
   */
  private async apiPost<T>(endpoint: string, bodyData: Object): Promise<T> {
    try {
      const fullUrl = `${this.XANO_ENDPOINT}${endpoint}`;
      console.log(`📤 POST Request to: ${fullUrl}`);
      console.log(`📤 Request body:`, JSON.stringify(bodyData, null, 2));
      console.log(`📤 Headers:`, Object.fromEntries(this.headers.entries()));

      const response = await fetch(fullUrl, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify(bodyData),
      });

      console.log(`📥 Response status: ${response.status} ${response.statusText}`);
      console.log(`📥 Response URL: ${response.url}`);

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) {
        console.log("Unauthorized request");
        throw new Error("Unauthorized request");
      }

      if (!handledResponse.ok) {
        const errorText = await handledResponse.text();
        console.error(`❌ Error response from ${endpoint}:`, errorText);
        console.error(`❌ Status: ${handledResponse.status} ${handledResponse.statusText}`);
        console.error(`❌ Full URL: ${fullUrl}`);
        
        // Try to parse error as JSON for better error messages
        let errorMessage = `API error: ${handledResponse.status} ${handledResponse.statusText}`;
        let errorData: any = {};
        
        try {
          errorData = JSON.parse(errorText);
          console.error(`❌ Parsed error JSON:`, errorData);
          
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.code) {
            if (errorData.code === "ERROR_CODE_NOT_FOUND") {
              errorMessage = `Endpoint not found: ${fullUrl}. Status: ${handledResponse.status}. Please verify the endpoint exists in your API.`;
            } else {
              errorMessage = errorData.code;
            }
            if (errorData.message) {
              errorMessage += `: ${errorData.message}`;
            }
          } else if (errorText) {
            errorMessage = errorText;
          }
        } catch (e) {
          // Error text is not JSON, use as-is
          console.error(`❌ Error text is not JSON:`, errorText);
          errorMessage = errorText || errorMessage;
        }
        
        const error = new Error(errorMessage);
        (error as any).status = handledResponse.status;
        (error as any).response = { data: errorData };
        (error as any).url = fullUrl;
        throw error;
      }

      const responseData = await handledResponse.json();
      console.log(`✅ Success response from ${endpoint}:`, responseData);
      return responseData;
    } catch (error: any) {
      console.error(`❌ Error posting to ${endpoint}:`, error);
      console.error(`❌ Error details:`, {
        message: error.message,
        status: error.status,
        url: error.url,
        response: error.response
      });
      throw error;
    }
  }

  /**
   * Generic PUT request handler.
   * @param {string} endpoint - The API endpoint.
   * @param {Object} bodyData - The request body data.
   * @returns {Promise<any>} The response data.
   */
  private async apiPut<T>(endpoint: string, bodyData: Object): Promise<T> {
    try {
      const fullUrl = `${this.XANO_ENDPOINT}${endpoint}`;
      console.log(`📤 PUT Request to: ${fullUrl}`);
      console.log(`📤 Request body:`, JSON.stringify(bodyData, null, 2));
      console.log(`📤 Headers:`, Object.fromEntries(this.headers.entries()));

      const response = await fetch(fullUrl, {
        method: "PUT",
        headers: this.headers,
        body: JSON.stringify(bodyData),
      });

      console.log(`📥 Response status: ${response.status} ${response.statusText}`);
      console.log(`📥 Response URL: ${response.url}`);

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) throw new Error("Unauthorized request");

      if (!handledResponse.ok) {
        const errorText = await handledResponse.text();
        console.error(`❌ Error response from ${endpoint}:`, errorText);
        console.error(`❌ Status: ${handledResponse.status} ${handledResponse.statusText}`);
        console.error(`❌ Full URL: ${fullUrl}`);
        
        // Try to parse error as JSON for better error messages
        let errorMessage = `API error: ${handledResponse.status} ${handledResponse.statusText}`;
        let errorData: any = {};
        
        try {
          errorData = JSON.parse(errorText);
          console.error(`❌ Parsed error JSON:`, errorData);
          
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.code) {
            if (errorData.code === "ERROR_CODE_NOT_FOUND") {
              errorMessage = `Endpoint not found: ${fullUrl}. Status: ${handledResponse.status}. Please verify the endpoint exists in your API.`;
            } else {
              errorMessage = errorData.code;
            }
            if (errorData.message) {
              errorMessage += `: ${errorData.message}`;
            }
          } else if (errorText) {
            errorMessage = errorText;
          }
        } catch (e) {
          // Error text is not JSON, use as-is
          console.error(`❌ Error text is not JSON:`, errorText);
          errorMessage = errorText || errorMessage;
        }
        
        const error = new Error(errorMessage);
        (error as any).status = handledResponse.status;
        (error as any).response = { data: errorData };
        (error as any).url = fullUrl;
        throw error;
      }

      const responseData = await handledResponse.json();
      console.log(`✅ Success response from ${endpoint}:`, responseData);
      return responseData;
    } catch (error: any) {
      console.error(`❌ Error putting to ${endpoint}:`, error);
      console.error(`❌ Error details:`, {
        message: error.message,
        status: error.status,
        url: error.url,
        response: error.response
      });
      throw error;
    }
  }

  /**
   * Generic PATCH request handler.
   * @param {string} endpoint - The API endpoint.
   * @param {Object} bodyData - The request body data.
   * @returns {Promise<any>} The response data.
   */
  private async apiPatch<T>(endpoint: string, bodyData: Object): Promise<T> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}${endpoint}`, {
        method: "PATCH",
        headers: this.headers,
        body: JSON.stringify(bodyData),
      });

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) throw new Error("Unauthorized request");

      return await response.json();
    } catch (error) {
      console.error(`Error patching to ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * Generic DELETE request handler.
   * @param {string} endpoint - The API endpoint.
   * @returns {Promise<void>} Resolves when the request is complete.
   */
  private async apiDelete(endpoint: string): Promise<void> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}${endpoint}`, {
        method: "DELETE",
        headers: this.headers,
      });

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) throw new Error("Unauthorized request");

      // No content expected from a DELETE request
    } catch (error) {
      console.error(`Error deleting ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * Fetches the audit log data from the API.
   * @returns {Promise<any>} - A promise that resolves with the audit log data.
   */
  public async getAuditLog(params: {
    page?: number;
    search?: string;
    userRole?: string;
    clientId?: number | string;
    filters?: {
      action?: string;
      user_id?: string;
      resource_type?: string;
      date_from?: string;
      date_to?: string;
    };
  }) {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page.toString());

      // ✅ NEW: Construct JSON structure for Xano filtering (like clients)
      const filterExpressions: any[] = [];

      // Add search filter if provided
      if (params.search) {
        filterExpressions.push({
          statement: {
            left: { tag: "col", operand: "audit_log.action" },
            op: "contains",
            right: { operand: params.search },
          },
        });
      }

      // Add other filters if provided
      if (params.filters) {
        Object.entries(params.filters)
          .filter(
            ([_, value]) =>
              value !== undefined && value !== null && value !== ""
          ) // Enhanced filter validation
          .forEach(([key, value]) => {
            let operand = `audit_log.${key}`;
            let operation = "=";
            let rightOperand = value;

            // Handle special filter types and operations
            if (key === "date_from") {
              operand = "audit_log.created_at";
              operation = ">=";
            } else if (key === "date_to") {
              operand = "audit_log.created_at";
              operation = "<=";
            } else if (key === "action") {
              operation = "contains"; // Search within action field
            } else if (key === "user_id") {
              operation = "="; // Exact match for user ID
            } else if (key === "resource_type") {
              operation = "="; // Exact match for resource type
            }

            console.log(
              `🔍 Adding audit filter: ${key} = ${JSON.stringify(rightOperand)} (operand: ${operand}, op: ${operation})`
            );

            filterExpressions.push({
              statement: {
                left: { tag: "col", operand },
                op: operation,
                right: { operand: rightOperand },
              },
            });
          });
      }

      // Determine endpoint and add client_id parameter for agent users
      let endpoint = `${this.XANO_ENDPOINT}audit_log`;

      // console.log("🔍 AUDIT LOG PARAMETERS DEBUG:");
      // console.log("🔍 params.userRole:", params.userRole);
      // console.log("🔍 params.clientId:", params.clientId);
      // console.log("🔍 typeof params.userRole:", typeof params.userRole);
      // console.log("🔍 typeof params.clientId:", typeof params.clientId);

      if (params.userRole === "agent" && params.clientId) {
        // Agent users: use client_id parameter
        queryParams.append("client_id", params.clientId.toString());
        console.log(
          `🔍 Agent user detected - using client_id: ${params.clientId}`
        );
        console.log(
          "🔍 Query params after adding client_id:",
          queryParams.toString()
        );
      } else if (params.userRole === "admin") {
        // Admin users: use agency-level endpoint (no client_id)
        // console.log(`🔍 Admin user detected - using agency-level endpoint`);
      } else {
        // Unknown role: default to agency-level endpoint
        console.log(
          `🔍 Unknown user role - defaulting to agency-level endpoint`
        );
        console.log(
          "🔍 Reason: userRole =",
          params.userRole,
          "clientId =",
          params.clientId
        );
      }

      // Add JSON filter to query params if we have any filters
      if (filterExpressions.length > 0) {
        queryParams.append(
          "search",
          JSON.stringify({ expression: filterExpressions })
        );
      }

      const fullUrl = `${endpoint}?${queryParams.toString()}`;
      console.log("🌐 AUDIT LOG API REQUEST URL:", fullUrl);

      const response = await fetch(fullUrl, {
        method: "GET",
        headers: this.headers,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch audit logs");
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching audit logs:", error);
      throw error;
    }
  }


  /**
   * Fetches all documents associated with a specific client ID.
   * @param {number | string} clientId - The ID of the client.
   * @returns {Promise<Array<any>>} - A promise that resolves with the documents data.
   */
  // public async getClientDocuments(clientId: number | string): Promise<any[]> {
  //   try {
  //     const response = await fetch(
  //       `${this.XANO_ENDPOINT}client/${clientId}/documents`,
  //       {
  //         method: "GET",
  //         headers: this.headers,
  //       }
  //     );

  //     if (!response.ok) {
  //       throw new Error("Failed to fetch client documents");
  //     }

  //     return await response.json();
  //   } catch (error) {
  //     console.error(
  //       `Error fetching documents for client with ID ${clientId}:`,
  //       error
  //     );
  //     throw error;
  //   }
  // }

  /**
   * Fetches all documents associated with a specific client ID.
   * For admin users: uses agency endpoint /agency/{agency_id}/document
   * For agent users: uses user endpoint /user/{user_id}/document
   * @param {number | string} clientId - The ID of the client (agency ID for admin, user ID for agent).
   * @param {string} userRole - The role of the user ('admin' or 'agent').
   * @returns {Promise<Array<any>>} - A promise that resolves with the documents data.
   */
  public async getClientDocuments(
    clientId: number | string,
    userRole?: string
  ): Promise<any[]> {
    try {
      // Determine endpoint priority based on user role
      let endpoints: string[] = [];

      if (userRole === "admin") {
        // Admin users: prioritize agency endpoints
        endpoints = [
          `${this.XANO_ENDPOINT}agency/${clientId}/document`, // Agency endpoint (singular)
          `${this.XANO_ENDPOINT}agency/${clientId}/documents`, // Agency endpoint (plural)
          `${this.XANO_ENDPOINT}user/${clientId}/document`, // Fallback to user endpoint
          `${this.XANO_ENDPOINT}user/${clientId}/documents`, // Fallback to user endpoint (plural)
        ];
        console.log(
          `🔍 Admin user detected - prioritizing agency endpoints for ID: ${clientId}`
        );
      } else if (userRole === "agent") {
        // Agent users: prioritize user endpoints
        endpoints = [
          `${this.XANO_ENDPOINT}user/${clientId}/document`, // User endpoint (singular)
          `${this.XANO_ENDPOINT}user/${clientId}/documents`, // User endpoint (plural)
          `${this.XANO_ENDPOINT}agency/${clientId}/document`, // Fallback to agency endpoint
          `${this.XANO_ENDPOINT}agency/${clientId}/documents`, // Fallback to agency endpoint (plural)
        ];
        console.log(
          `🔍 Agent user detected - prioritizing user endpoints for ID: ${clientId}`
        );
      } else {
        // Unknown role: try all endpoints
        endpoints = [
          `${this.XANO_ENDPOINT}agency/${clientId}/document`, // Agency endpoint (singular)
          `${this.XANO_ENDPOINT}agency/${clientId}/documents`, // Agency endpoint (plural)
          `${this.XANO_ENDPOINT}user/${clientId}/document`, // User endpoint (singular)
          `${this.XANO_ENDPOINT}user/${clientId}/documents`, // User endpoint (plural)
          `${this.XANO_ENDPOINT}client/${clientId}/documents`, // Client endpoint (plural)
          `${this.XANO_ENDPOINT}client/${clientId}/document`, // Client endpoint (singular)
        ];
        console.log(
          `🔍 Unknown user role - trying all endpoints for ID: ${clientId}`
        );
      }

      for (const url of endpoints) {
        // console.log(`🔍 Trying endpoint: ${url}`);

        try {
          const response = await fetch(url, {
            method: "GET",
            headers: this.headers,
          });

          // console.log(`🔍 Response status: ${response.status}`);
          // console.log(`🔍 Response ok: ${response.ok}`);

          if (response.ok) {
            const data = await response.json();
            console.log(`✅ Success with endpoint: ${url}`);
            // console.log(`🔍 Client documents response:`, data);

            // Handle different response formats
            if (Array.isArray(data)) {
              return data;
            } else if (data && Array.isArray(data.documents)) {
              return data.documents;
            } else if (data && Array.isArray(data.items)) {
              return data.items;
            } else if (data && typeof data === "object") {
              // If it's an object but not an array, return it as a single item array
              return [data];
            } else {
              console.log(
                `🔍 Unexpected response format, returning empty array`
              );
              return [];
            }
          } else {
            const errorText = await response.text();
            console.log(
              `❌ ${url} failed with ${response.status}: ${errorText}`
            );

            // If this is the original endpoint, log detailed error info
            if (url === `${this.XANO_ENDPOINT}client/${clientId}/documents`) {
              console.error(`❌ Original endpoint error details:`);
              console.error(
                `❌ Request Headers:`,
                Object.fromEntries(this.headers.entries())
              );
              console.error(
                `❌ Response Headers:`,
                Object.fromEntries(response.headers.entries())
              );

              try {
                const errorJson = JSON.parse(errorText);
                console.error(`❌ Parsed Error Response:`, errorJson);
              } catch (e) {
                console.error(`❌ Raw Error Response:`, errorText);
              }
            }
          }
        } catch (fetchError) {
          console.log(`❌ ${url} fetch error:`, fetchError);
        }
      }

      // If all endpoints failed, return empty array instead of throwing error
      console.log(
        `⚠️ All client document endpoints failed for client ID: ${clientId}, returning empty array`
      );
      return [];
    } catch (error) {
      console.error(
        `Error fetching documents for client with ID ${clientId}:`,
        error
      );
      // Return empty array instead of throwing error
      console.log(`⚠️ Returning empty array due to error`);
      return [];
    }
  }

  /**
   * Fetches all API keys for the current user.
   * @returns {Promise<Array<{ id: string, name: string, permissions: string }>>} A promise resolving with the API keys data.
   */
  public async getApiKeys(): Promise<
    Array<{ id: string; name: string; permissions: string }>
  > {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}api_keys`, {
        method: "GET",
        headers: this.headers,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch API keys.");
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching API keys:", error);
      throw error;
    }
  }

  /**
   * Creates a new API key.
   * @param {Object} apiKeyData - The data for the new API key.
   * @param {string} apiKeyData.name - The name of the API key.
   * @param {string} apiKeyData.permissions - The permissions for the API key (e.g., "read-only" or "read-write").
   * @returns {Promise<{ id: string, name: string, key: string, permissions: string }>} The created API key data.
   */
  public async createApiKey(apiKeyData: {
    name: string;
    permissions: string;
  }): Promise<{ id: string; name: string; key: string; permissions: string }> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}api_keys`, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify(apiKeyData),
      });

      if (!response.ok) {
        throw new Error("Failed to create API key.");
      }

      return await response.json();
    } catch (error) {
      console.error("Error creating API key:", error);
      throw error;
    }
  }

  /**
   * Deletes an API key by its ID.
   * @param {string} apiKeyId - The ID of the API key to delete.
   * @returns {Promise<void>} Resolves when the API key is deleted successfully.
   */
  public async deleteApiKey(apiKeyId: string): Promise<void> {
    try {
      const response = await fetch(
        `${this.XANO_ENDPOINT}api_keys/${apiKeyId}`,
        {
          method: "DELETE",
          headers: this.headers,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete API key.");
      }

      console.log(`API key with ID ${apiKeyId} deleted successfully.`);
    } catch (error) {
      console.error(`Error deleting API key with ID ${apiKeyId}:`, error);
      throw error;
    }
  }

  /**
   * Fetches API keys for a specific user by their ID.
   * @param {number | string} userId - The ID of the user.
   * @returns {Promise<ApiKeyValues[]>} - A promise that resolves to the API keys of the user.
   */
  public async getApiKeysByUserId(
    userId: number | string
  ): Promise<ApiKeyValues[]> {
    try {
      const response = await fetch(
        `${this.XANO_ENDPOINT}user/${userId}/api_keys`,
        {
          method: "GET",
          headers: this.headers,
        }
      );

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) throw new Error("Unauthorized request");

      const data = await handledResponse.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error(
        `Error fetching API keys for user with ID ${userId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Fetches all engagement records from the API.
   * @returns {Promise<Array<EngagementType>>} An array of engagement records.
   */
  public async getEngagements(): Promise<Array<EngagementType>> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}engagements`, {
        method: "GET",
        headers: this.headers,
      });

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) throw new Error("Unauthorized request");

      return await handledResponse.json();
    } catch (error) {
      console.error("Error fetching engagements:", error);
      throw error;
    }
  }

  /**
   * Adds a new engagement record to the API.
   * @param {Partial<EngagementType>} engagementData - The data for the new engagement.
   * @returns {Promise<EngagementType>} The newly added engagement data.
   */
  public async addEngagement(
    engagementData: Partial<EngagementType>
  ): Promise<EngagementType> {
    try {
      const response = await fetch(`${this.XANO_ENDPOINT}engagements`, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify(engagementData),
      });

      const handledResponse = this.handleResponse(response);
      if (!handledResponse) {
        throw new Error("Unauthorized request");
      }

      return await handledResponse.json();
    } catch (error) {
      console.error("Error adding new engagement:", error);
      throw error;
    }
  }

  /**
   * Deletes an engagement by its ID.
   * @param {number} engagementId - The ID of the engagement to delete.
   * @returns {Promise<void>} Resolves when the engagement is deleted successfully.
   */
  public async deleteEngagement(engagementId: number): Promise<void> {
    try {
      const response = await fetch(
        `${this.XANO_ENDPOINT}engagements/${engagementId}`,
        {
          method: "DELETE",
          headers: this.headers,
        }
      );

      if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(
          errorResponse.message || "Failed to delete the engagement."
        );
      }

      console.log(`Engagement with ID ${engagementId} deleted successfully.`);
    } catch (error) {
      console.error(
        `Error occurred while deleting engagement with ID ${engagementId}:`,
        error
      );
      throw error;
    }
  }

  public async getCommissions(userId?: number): Promise<Commission[]> {
    try {
      // Use agent_commissions endpoint to match the pattern of other working endpoints
      const endpoint = userId
        ? `agent_commissions?user_id=${userId}`
        : "agent_commissions";
      return this.apiGet<Commission[]>(endpoint);
    } catch (error) {
      console.error("Error fetching commissions:", error);
      return [];
    }
  }

  public async getCommissionsByStatus(
    status: "pending" | "approved" | "paid"
  ): Promise<Commission[]> {
    try {
      return this.apiGet<Commission[]>(`agent_commissions?status=${status}`);
    } catch (error) {
      console.error(`Error fetching commissions with status ${status}:`, error);
      return [];
    }
  }

  public async createCommission(commissionData: {
    user_id: number;
    client_id: number;
    amount: number;
    commission_rate: number;
    client_name: string;
  }): Promise<Commission> {
    try {
      return this.apiPost<Commission>("agent_commissions", {
        ...commissionData,
        status: "pending",
        created_at: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error creating commission:", error);
      throw error;
    }
  }

  public async approveCommission(commissionId: number): Promise<Commission> {
    try {
      return this.apiPut<Commission>(
        `agent_commissions/${commissionId}/approve`,
        {
          status: "approved",
          approved_at: new Date().toISOString(),
        }
      );
    } catch (error) {
      console.error(`Error approving commission ${commissionId}:`, error);
      throw error;
    }
  }

  public async getPayouts(userId?: number): Promise<Payout[]> {
    try {
      // Try using agent_commission_payouts endpoint
      const endpoint = userId
        ? `agent_commission_payouts?user_id=${userId}`
        : "agent_commission_payouts";
      console.log("Fetching payouts from endpoint:", endpoint);

      const result = await this.apiGet<any>(endpoint);
      console.log("Payouts API response:", result);

      // Handle both array and object responses
      if (Array.isArray(result)) {
        return result;
      } else if (result && typeof result === "object") {
        // If it's an object with items array
        if (Array.isArray(result.items)) {
          return result.items;
        }
        // If it's just an object, return as single item array
        return [result];
      }

      // Default empty array
      return [];
    } catch (error) {
      console.error("Error fetching payouts:", error);
      return [];
    }
  }

  public async getPayoutsByStatus(
    status: "pending" | "completed" | "cancelled"
  ): Promise<Payout[]> {
    try {
      const endpoint = `agent_commission_payouts?status=${status}`;
      console.log("Fetching payouts by status from endpoint:", endpoint);

      const result = await this.apiGet<any>(endpoint);

      // Handle both array and object responses
      if (Array.isArray(result)) {
        return result;
      } else if (result && typeof result === "object") {
        // If it's an object with items array
        if (Array.isArray(result.items)) {
          return result.items;
        }
        // If it's just an object, return as single item array
        return [result];
      }

      // Default empty array
      return [];
    } catch (error) {
      console.error(`Error fetching payouts with status ${status}:`, error);
      return [];
    }
  }

  public async createPayout(payoutData: {
    user_id: number;
    amount: number;
    commissions_paid: number[];
    description?: string;
  }): Promise<Payout> {
    console.log("Creating payout with data:", payoutData);
    try {
      // Try agent_commission_payouts endpoint instead
      return this.apiPost<Payout>("agent_commission_payouts", {
        ...payoutData,
        status: "pending",
        created_at: new Date().toISOString(),
        created_by: this.getUserId(),
      });
    } catch (error) {
      console.error("Error in createPayout:", error);
      throw error;
    }
  }

  public async completePayout(payoutId: number): Promise<Payout> {
    try {
      return this.apiPut<Payout>(
        `agent_commission_payouts/${payoutId}/complete`,
        {
          status: "completed",
          completed_at: new Date().toISOString(),
        }
      );
    } catch (error) {
      console.error(`Error completing payout ${payoutId}:`, error);
      throw error;
    }
  }

  public async cancelPayout(payoutId: number): Promise<Payout> {
    try {
      return this.apiPut<Payout>(
        `agent_commission_payouts/${payoutId}/cancel`,
        {
          status: "cancelled",
          cancelled_at: new Date().toISOString(),
        }
      );
    } catch (error) {
      console.error(`Error cancelling payout ${payoutId}:`, error);
      throw error;
    }
  }

  /**
   * Fetches agent commissions with optional filtering by status.
   * @param {Object} options - Optional parameters for filtering.
   * @param {number} [options.userId] - Filter by specific user ID.
   * @param {string} [options.status] - Filter by status (e.g., "approved").
   * @returns {Promise<Array<AgentCommission>>} List of agent commissions.
   */
  public async getAgentCommissions(
    options: {
      userId?: number;
      status?: string;
    } = {}
  ): Promise<Array<AgentCommission>> {
    try {
      const queryParams = new URLSearchParams();

      if (options.userId) {
        queryParams.append("user_id", options.userId.toString());
      }

      if (options.status) {
        queryParams.append("status", options.status);
      }

      const queryString = queryParams.toString()
        ? `?${queryParams.toString()}`
        : "";
      return this.apiGet<Array<AgentCommission>>(
        `agent_commissions${queryString}`
      );
    } catch (error) {
      console.error("Error fetching agent commissions:", error);
      throw error;
    }
  }

  /**
   * Approves (marks as paid) an agent commission by its ID.
   * @param {string} commissionId - The ID of the agent commission to approve.
   * @returns {Promise<AgentCommission>} The updated agent commission data.
   */
  public async approveAgentCommission(
    commissionId: string
  ): Promise<AgentCommission> {
    try {
      return this.apiPut<AgentCommission>(`agent_commissions/${commissionId}`, {
        status: "approved",
      });
    } catch (error) {
      console.error(`Error approving agent commission ${commissionId}:`, error);
      throw error;
    }
  }

  /**
   * Approves a commission for a client, triggering the creation of a new commission record.
   * @param {number} clientId - The ID of the client.
   * @param {number} agentCommission - Optional commission amount if different from existing.
   * @returns {Promise<ClientType>} The updated client data.
   */
  public async approveClientCommission(
    clientId: number,
    agentCommission?: number
  ): Promise<ClientType> {
    try {
      const updateData: Partial<ClientType> = {
        commission_approved: true,
      };

      // If agent_commission is provided, update it
      if (agentCommission !== undefined) {
        updateData.agent_commission = agentCommission;
      }

      return this.apiPut<ClientType>(`client/${clientId}`, updateData);
    } catch (error) {
      console.error(
        `Error approving commission for client ${clientId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Pays out a commission for a client, creating a payout record.
   * @param {number} clientId - The ID of the client.
   * @param {number} amount - The amount to pay out.
   * @returns {Promise<{ success: boolean, payoutId?: string }>} The result of the payout operation.
   */
  public async payOutClientCommission(
    clientId: number,
    amount: number
  ): Promise<{ success: boolean; payoutId?: string }> {
    try {
      // Mock implementation - in a real implementation, this would call an API endpoint
      console.log(`Paying out commission for client ${clientId}: ${amount}`);

      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Return mock success response
      return {
        success: true,
        payoutId: `payout-${Date.now()}-${clientId}`,
      };
    } catch (error) {
      console.error(
        `Error paying out commission for client ${clientId}:`,
        error
      );
      return { success: false };
    }
  }

  /**
   * Fetches all pending commissions that resulted from client approvals.
   * @returns {Promise<Array<AgentCommission>>} List of pending agent commissions.
   */
  public async getPendingCommissions(): Promise<Array<AgentCommission>> {
    return this.getAgentCommissions({ status: "pending" });
  }

  /**
   * Get payouts for an agent
   * @param {number} userId - Optional user ID to filter payouts
   * @returns {Promise<{ success: boolean; data: AgentPayout[] }>} List of payouts for the agent.
   */
  public async getAgentPayouts(
    userId?: number
  ): Promise<{ success: boolean; data: AgentPayout[] }> {
    try {
      const endpoint = userId
        ? `/agent/payouts?user_id=${userId}`
        : "/agent/payouts";
      const response = await this.apiGet<{ data: AgentPayout[] }>(endpoint);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error fetching agent payouts:", error);
      return { success: false, data: [] };
    }
  }

  /**
   * Creates a new payout for an agent.
   * @param {Object} payoutData - The data for the new payout.
   * @param {number} payoutData.user_id - The ID of the agent.
   * @param {number} payoutData.amount - The amount to pay out.
   * @param {string[]} payoutData.commissions_paid - Array of commission IDs to pay out.
   * @param {string} [payoutData.description] - Optional description for the payout.
   * @returns {Promise<CommissionPayout>} The created payout data.
   */
  public async createAgentPayout(payoutData: {
    user_id: number;
    amount: number;
    commissions_paid: string[];
    description?: string;
  }): Promise<CommissionPayout> {
    try {
      return this.apiPost<CommissionPayout>("agent_payouts", {
        ...payoutData,
        status: "pending",
        created_at: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error creating agent payout:", error);
      throw error;
    }
  }

  /**
   * Updates the status of a payout to completed.
   * @param {string} payoutId - The ID of the payout to complete.
   * @returns {Promise<CommissionPayout>} The updated payout data.
   */
  public async completeAgentPayout(
    payoutId: string
  ): Promise<CommissionPayout> {
    try {
      return this.apiPut<CommissionPayout>(`agent_payouts/${payoutId}`, {
        status: "completed",
        completed_at: new Date().toISOString(),
      });
    } catch (error) {
      console.error(`Error completing payout ${payoutId}:`, error);
      throw error;
    }
  }

  /**
   * Updates the status of a payout to cancelled.
   * @param {string} payoutId - The ID of the payout to cancel.
   * @returns {Promise<CommissionPayout>} The updated payout data.
   */
  public async cancelAgentPayout(payoutId: string): Promise<CommissionPayout> {
    try {
      return this.apiPut<CommissionPayout>(`agent_payouts/${payoutId}`, {
        status: "cancelled",
        cancelled_at: new Date().toISOString(),
      });
    } catch (error) {
      console.error(`Error cancelling payout ${payoutId}:`, error);
      throw error;
    }
  }

  /**
   * Broadcasts a client lock event to other users via Pusher through Xano's messages/new endpoint.
   * This endpoint triggers Pusher events on the agency-specific channel for real-time lock synchronization.
   *
   * @param {Object} payload - The lock event payload
   * @param {string} payload.name - The event name (e.g., 'client-locked', 'client-unlocked', 'client-expired')
   * @param {string} payload.message - JSON stringified event data containing clientId, userId, userName, action, timestamp
   * @returns {Promise<any>} The response from the Pusher broadcast
   * @throws {Error} If the broadcast fails or returns a non-OK status
   *
   * @example
   * ```typescript
   * await api.broadcastClientLockEvent({
   *   name: 'client-locked',
   *   message: JSON.stringify({
   *     clientId: '123',
   *     userId: '456',
   *     userName: 'John Doe',
   *     action: 'locked',
   *     timestamp: Date.now()
   *   })
   * });
   * ```
   */
  public async broadcastClientLockEvent(payload: {
    name: string;
    message: string;
  }): Promise<any> {
    // Lambda function automatically uses $var.currentUser.agency_id for channel construction
    return this.apiPost<any>("messages/new", {
      name: payload.name,
      message: payload.message,
    });
  }

  /**
   * Broadcasts a dashboard layout update event via Pusher through Xano's messages/new endpoint.
   * This triggers Pusher events on the user-specific private channel for real-time layout synchronization.
   *
   * @param {Object} payload - The dashboard layout event payload
   * @param {string} payload.name - The event name (should be 'dashboard-layout-updated')
   * @param {string} payload.message - JSON stringified event data containing layout, userId, timestamp
   * @returns {Promise<any>} The response from the Pusher broadcast
   * @throws {Error} If the broadcast fails or returns a non-OK status
   */
  public async broadcastDashboardLayoutUpdate(payload: {
    name: string;
    message: string;
  }): Promise<any> {
    return this.apiPost<any>("messages/new", {
      name: payload.name,
      message: payload.message,
    });
  }

  /**
   * Authenticate Pusher channel subscription
   * Used by PusherService to get authentication signature for private channels
   * @param {string} socketId - The Pusher socket ID
   * @returns {Promise<{auth: string, socket_id: string, channel_name: string}>}
   */
  public async authenticatePusherChannel(socketId: string, channelName?: string): Promise<{
    auth: string;
    socket_id: string;
    channel_name: string;
  }> {
    const payload: any = {
      socket_id: socketId,
    };
    
    // CRITICAL: Include channel_name for proper signature generation
    // Pusher requires the channel name to generate the correct HMAC signature
    // The backend must use: HMAC-SHA256(socket_id + ":" + channel_name, PUSHER_SECRET)
    if (channelName) {
      payload.channel_name = channelName;
      console.log(`🔐 Authenticating Pusher channel: ${channelName} with socket_id: ${socketId}`);
    } else {
      console.warn("⚠️ Warning: Authenticating Pusher channel without channel_name - this may cause signature errors");
    }
    
    try {
      const response = await this.apiPost<any>("messages/authenticate", payload);
      
      // Validate response format
      if (!response || !response.auth) {
        console.error("❌ Invalid auth response from backend:", response);
        throw new Error("Invalid auth response: missing 'auth' field");
      }
      
      // Verify auth string format (should be "app_key:signature")
      const authParts = response.auth.split(':');
      if (authParts.length !== 2) {
        console.error("❌ Invalid auth string format:", response.auth);
        throw new Error("Invalid auth string format: expected 'app_key:signature'");
      }
      
      console.log(`✅ Auth response validated - app_key: ${authParts[0]}, signature length: ${authParts[1].length}`);
      
      return response;
    } catch (error: any) {
      console.error("❌ Pusher authentication failed:", error);
      console.error("❌ Request payload:", payload);
      throw error;
    }
  }
}
